import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  startTransition,
} from "react";
import {
  LuVideo,
  LuMusic,
  LuType,
  LuImage,
  LuSquare,
  LuLayoutGrid,
  LuMusic2,
  LuLayers,
  LuListChecks,
  LuTable,
  LuChartBar,
  LuCirclePlay,
  LuMousePointer,
  LuMegaphone,
  LuMicVocal,
  LuListOrdered,
  LuCircleCheck,
  LuMove,
  LuArrowLeftRight,
  LuWand,
  LuUser,
  LuPenLine,
  LuColumns2,
  LuHeading,
  LuEye,
  LuEyeOff,
  LuLock,
  LuLockOpen,
  LuChevronRight,
  LuChevronDown,
  LuMinus,
  LuPlus,
  LuSquareDashed,
  LuZap,
  LuGripVertical,
} from "react-icons/lu";
import Toolbar from "./components/Toolbar";
import { SPEED_PRESETS, YOUTUBE_MAX_SPEED } from "./components/SpeedControl";
import Ruler from "./components/Ruler";
import TrackClip from "./components/TrackClip";
import SubRow from "./components/SubRow";
import { nextSubFieldSelection } from "./utils/subFieldSelection";
import VideoThumb from "./components/VideoThumb";
import { PlayheadLine } from "./components/Playhead";
import { buildLayersFromBoxes, onMediaDurationCached } from "./utils/buildLayers";
import { groupLayersIntoRows, rowKeyForLayer, GROUPABLE_TYPES } from "./utils/groupRows";
import { resolveRightResizeRipple, resolveRowInsertion } from "./utils/joinPlacement";
import { resolveHeadTrim } from "./utils/trimHead";
import { computeSnapThresholdFrames, resolveStickySnap } from "./utils/snap";
import { createEdgeAutoScroll } from "./utils/edgeAutoScroll";
import { getYoutubeThumbnailUrl, isYoutubeUrl } from "../../../../common/youtube_tools";
import {
  registerPlayheadTracker,
  unregisterPlayheadTracker,
  registerPlayheadSeeker,
  unregisterPlayheadSeeker,
} from "../../../../common/playhead_tracker.es6";
import { SceneClock, secToFrame } from "../../../../common/scene_clock.es6";
import { MediaGate } from "../../../../common/media_gate.es6";
import { elementHolder } from "../../../../common/media_holders.es6";
import { unlockAudioGraph } from "../../../../common/audio_graph.es6";
import {
  createVideoSyncState,
  driveVideoSync,
  releaseVideoSync,
} from "../../../../common/video_sync.es6";
import {
  createAudioSyncState,
  driveAudioSync,
  resetAudioSync,
  seekAudioSync,
} from "../../../../common/audio_sync.es6";
import i18n from "i18next";
import "./index.scss";

const FPS = 30;
const PIXELS_PER_SECOND = 50;
const AUDIO_TYPES = new Set(["audio", "scene-audio"]);
// An AudioCue the learner pressed during the preview (marked by the cue itself): the timeline
// leaves it alone until an explicit seek hands it back.
function learnerCue(el, seek) {
  if (el.dataset.learnerCue !== "1") return false;
  if (!seek) return true;
  delete el.dataset.learnerCue;
  delete el.dataset.pausedByPreview;
  return false;
}

const MEDIA_TYPES = new Set(["audio", "scene-audio", "video"]); // Audio ve video için otomatik play/stop
const LABEL_WIDTH = 150;
// Zoom bounds. Manual steps stay in 0.25 increments above MIN_STEP_ZOOM; below
// it (only reachable via fit-to-scene on long scenes) steps are multiplicative.
const MIN_ZOOM = 0.02;
const MAX_ZOOM = 5;
const MIN_STEP_ZOOM = 0.25;
// Matches the trailing gutter totalWidth adds past the timeline span.
const TRACK_END_GUTTER = 120;
// Band around a row edge (px) that targets "drop between rows" instead of
// "drop into this row" during a clip drag.
const BOUNDARY_PX = 7;

const TRACK_COLORS = {
  video: "#3d7eff",
  audio: "#1db954",
  text: "#f5a623",
  image: "#9b59b6",
  shape: "#e74c3c",
  element: "#1abc9c",
  "scene-audio": "#27ae60",
};

const TRACK_ICONS = {
  video: LuVideo,
  audio: LuMegaphone,
  text: LuType,
  image: LuImage,
  shape: LuSquare,
  element: LuLayoutGrid,
  "scene-audio": LuMicVocal,
};

// Plugin ID → React Icon component
const PLUGIN_ICONS = {
  multipleanswer: LuListChecks,
  datatable: LuTable,
  graficad3: LuChartBar,
  enrichedplayer: LuCirclePlay,
  oneclick: LuMousePointer,
  audiocue: LuMegaphone,
  ordering: LuListOrdered,
  truefalse: LuCircleCheck,
  dragdrop: LuMove,
  matching: LuArrowLeftRight,
  animationoptions: LuWand,
  characters: LuUser,
  fillinblank: LuPenLine,
  hotspotimages: LuImage,
  hotspotsimage: LuImage,
  sortable_container: LuColumns2,
  basictext: LuType,
  textplayer: LuType,
  title: LuHeading,
  interactiveplugin: LuZap,
};

const SPEED_STORAGE_KEY = "aidea.timeline.previewSpeed";

function readStoredSpeed() {
  try {
    const stored = parseFloat(localStorage.getItem(SPEED_STORAGE_KEY));
    return SPEED_PRESETS.includes(stored) ? stored : 1;
  } catch {
    return 1;
  }
}

function formatTime(frame) {
  const total = frame / FPS;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  const cs = Math.floor((total % 1) * 100);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
}

const IdeaTimeline = forwardRef(function IdeaTimeline(
  {
    boxes,
    store,
    navItemSelected,
    containedViewSelected,
    duration: propDuration,
    selectedBoxId,
    onSelectLayer,
    playAnimation,
    pauseAnimation,
    setCurrentTime,
    driveNarration,
    narrationHolders,
    onAnimationReset,
    onTriggerAnimation,
    onTriggerLayerAnimation,
    onLayerUpdate,
    onLayerTrackChange,
    onBatchTimelineUpdate,
    onLayerVisibilityToggle,
    onLockToggle,
    onToggleLoop,
    onSplitLayer,
    onSyncScene,
    onMotionizeScene,
    syncingScene,
    syncSceneStatus,
    collapsed,
    onToggleCollapse,
    knollingViewVisible,
    onToggleKnolling,
    onDeleteLayer,
    onLayerReorder,
    onSceneAudioReorder,
    onLayerDoubleClick,
    onDurationChange,
    onPersistLayerEnds,
    onOpenMerlin,
    onOpenAnimation,
    onToggleVideoLoop,
    onSelectSubField,
    onSubFieldTimingChange,
    onAddVideoInteraction,
    onPlaybackSpeedChange,
  },
  ref
) {
  const [duration, setDuration] = useState(propDuration || 1800);
  const [durationLocked, setDurationLocked] = useState(false);
  const toggleDurationLock = () => setDurationLocked((prev) => !prev);

  useEffect(() => {
    if (propDuration && propDuration !== duration) {
      setDuration(propDuration);
    }
  }, [propDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const state = store?.getState()?.undoGroup?.present || {};
    const animationsById = state.animationsById || {};
    const animationList = Object.values(boxes || {})
      .filter((box) => animationsById[box.id] !== undefined)
      .map((box) => ({ id: box.id, animations: animationsById[box.id] }));
    const textAnimationList = Object.values(boxes || {})
      .filter((box) => box.textAnimation != null)
      .map((box) => ({ id: box.id, textAnimation: box.textAnimation }));
    const boxAnimationList = Object.values(boxes || {})
      .filter((box) => box.boxAnimation != null || box.boxExitAnimation != null)
      .map((box) => ({
        id: box.id,
        boxAnimation: box.boxAnimation ?? null,
        boxExitAnimation: box.boxExitAnimation ?? null,
      }));
  }, [boxes, navItemSelected, containedViewSelected, propDuration, selectedBoxId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Redux state ──────────────────────────────────────────
  const storeState = store?.getState()?.undoGroup?.present || {};
  const animations = storeState.animationsById || {};
  const navItemsById = storeState.navItemsById || {};
  const containedViewsById = storeState.containedViewsById || {};
  const soundList = storeState.soundList || {};
  const pluginToolbars = storeState.pluginToolbarsById || {};
  const pluginToolbarsRef = useRef(pluginToolbars);
  pluginToolbarsRef.current = pluginToolbars;

  const currentScene =
    containedViewSelected && containedViewSelected !== 0 ? containedViewSelected : navItemSelected;

  // ── Layer building ───────────────────────────────────────
  const [layers, setLayers] = useState([]);
  const isDraggingRef = useRef(false);
  const userSetDurationRef = useRef(false);
  const durationRef = useRef(duration);
  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);
  // Persisted scene duration (0 = never set) — kept in a ref so the rebuild
  // effect's bootstrap gate never reads a stale closure value.
  const propDurationRef = useRef(propDuration);
  useEffect(() => {
    propDurationRef.current = propDuration;
  }, [propDuration]);
  // Track last value sent to Redux to avoid re-triggering rebuild loop
  const lastSavedDurationRef = useRef(propDuration || 1800);

  // Redux changes arriving while the drag gate is up (e.g. the drag's own
  // editSound commit re-sorting soundList) must not be dropped — queue them
  // and replay one rebuild when the gate clears. Otherwise the stale row
  // order survives until an unrelated change (like a marker move) reveals it.
  const rebuildQueuedRef = useRef(false);
  const [rebuildTick, setRebuildTick] = useState(0);
  // Monotonic build id — an async build only lands if it is still the latest.
  const buildSeqRef = useRef(0);

  // Snapshot of the previous rebuild's layer ids, per scene — used to detect
  // which clips are NEW so they can be revealed and flashed.
  const prevLayerIdsRef = useRef(null);
  // Flash is state-driven (className prop): the re-renders right after
  // ADD_BOX rewrite className, wiping any imperatively added class.
  const [flashLayerIds, setFlashLayerIds] = useState(null);
  const flashTimerRef = useRef(null);
  const revealNewLayers = (fresh) => {
    setFlashLayerIds(new Set(fresh.map((l) => l.id)));
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashLayerIds(null), 3400);
    // Wait one tick so the new clips are in the DOM.
    setTimeout(() => {
      const area = tracksAreaRef.current;
      const tracksEl = tracksElRef.current;
      if (!area || !tracksEl) return;
      const aRect = area.getBoundingClientRect();
      let scrollLeft = null;
      let scrollTop = null;

      const first = fresh[0];
      // Rows can hold several clips (shared media rows) — address the clip
      // itself and derive its row from it.
      const clip = tracksEl.querySelector(`.it-clip[data-layer-id="${first.id}"]`);
      const row = clip?.closest(".it-track-row");
      if (row) {
        const rRect = row.getBoundingClientRect();
        // 28 ≈ sticky ruler height; keep the row clear of it and of the bottom edge.
        if (rRect.bottom > aRect.bottom - 16) {
          scrollTop = area.scrollTop + (rRect.bottom - (aRect.bottom - 16));
        } else if (rRect.top < aRect.top + 28) {
          scrollTop = area.scrollTop - (aRect.top + 28 - rRect.top);
        }
      }
      if (clip) {
        const cRect = clip.getBoundingClientRect();
        const visibleLeft = aRect.left + LABEL_WIDTH;
        // Bring the clip's start into view with a little breathing room.
        if (cRect.left < visibleLeft + 8 || cRect.left > aRect.right - 80) {
          scrollLeft = area.scrollLeft + (cRect.left - (visibleLeft + 40));
        }
      }
      if (scrollLeft !== null || scrollTop !== null) {
        area.scrollTo({
          left: scrollLeft !== null ? Math.max(0, scrollLeft) : area.scrollLeft,
          top: scrollTop !== null ? Math.max(0, scrollTop) : area.scrollTop,
          behavior: "smooth",
        });
      }
    }, 80);
  };

  useEffect(() => {
    if (isDraggingRef.current) {
      rebuildQueuedRef.current = true;
      return;
    }

    if (!boxes || Object.keys(boxes).length === 0) {
      setLayers([]);
      // Seed so the first element of a brand-new project still flashes.
      prevLayerIdsRef.current = { scene: currentScene, ids: new Set() };
      return;
    }

    const rebuild = () => {
      const seq = ++buildSeqRef.current;
      buildLayersFromBoxes({
        boxes,
        animations,
        currentScene,
        navItemsById,
        soundList,
        pluginToolbars,
        fps: FPS,
        defaultDuration: durationRef.current,
      }).then((result) => {
        // A newer build (scene switch, redux change, duration-cache fill)
        // superseded this one — never paint stale layers over it.
        if (seq !== buildSeqRef.current) return;
        if (isDraggingRef.current) {
          // Drag started mid-build — this result is stale; replay after the gate.
          rebuildQueuedRef.current = true;
        } else {
          const builtLayers = result;

          // buildLayers sorts back-to-front (the `.boxes` axis); the timeline
          // shows the FRONTMOST row on top, matching the Layers panel and the
          // usual video-editor convention, so drag-up == bring forward.
          setLayers([...builtLayers].reverse());

          // Fresh-clip reveal: layers new to an incremental rebuild of the
          // same scene get scrolled into view and flashed.
          const prevSnap = prevLayerIdsRef.current;
          const nextIds = new Set(builtLayers.map((l) => l.id));
          // Empty prev set still flashes (first element of an empty scene);
          // only a scene switch / first mount skips.
          if (prevSnap && prevSnap.scene === currentScene) {
            const fresh = builtLayers.filter((l) => !prevSnap.ids.has(l.id));
            if (fresh.length > 0 && fresh.length <= 3) revealNewLayers(fresh);
          }
          prevLayerIdsRef.current = { scene: currentScene, ids: nextIds };

          // Bootstrap only: adopt the content end while the scene has NO
          // persisted duration yet. A persisted duration is the user's marker —
          // growing content (e.g. a longer narration) must never move it.
          if (!userSetDurationRef.current && !(propDurationRef.current > 0)) {
            // A loopScene clip bar keeps its natural (one-period) span — it
            // must not inflate the bootstrapped scene duration it's exempt from.
            const maxFrame = builtLayers.reduce((m, l) => {
              if (l.type === "scene-audio" && l.audioData?.loopScene) return m;
              return Math.max(m, l.endFrame || 0);
            }, 0);
            if (maxFrame > 0 && maxFrame !== durationRef.current) {
              lastSavedDurationRef.current = maxFrame;
              setDuration(maxFrame);
              onDurationChange?.(maxFrame);
            }
          }

          // One-time migration: freeze legacy endFrame-less boxes at their
          // currently displayed end, so content growth can never stretch
          // them again. Media layers are skipped (their end is media-derived).
          const missingEnds = builtLayers.filter(
            (l) =>
              l.type !== "scene-audio" &&
              l.type !== "audio" &&
              l.type !== "video" &&
              boxes[l.id] &&
              typeof boxes[l.id]?.timeline?.endFrame !== "number" &&
              typeof l.endFrame === "number" &&
              l.endFrame > 0
          );
          if (missingEnds.length > 0) {
            onPersistLayerEnds?.(missingEnds.map((l) => ({ id: l.id, endFrame: l.endFrame })));
          }
        }
      });
    };

    rebuild();

    // Yeni bir media süresinin cache'e girmesi durumunda layers'ı yeniden oluştur
    const unsubscribe = onMediaDurationCached(() => rebuild());
    return () => unsubscribe();
  }, [boxes, animations, currentScene, navItemsById, soundList, pluginToolbars, rebuildTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drop the drag gate and replay any rebuild that was queued behind it.
  const releaseDragGate = () => {
    isDraggingRef.current = false;
    if (rebuildQueuedRef.current) {
      rebuildQueuedRef.current = false;
      setRebuildTick((t) => t + 1);
    }
  };

  // keep a ref to latest layers for use inside intervals/callbacks
  const layersRef = useRef(layers);
  const resizeRippleRef = useRef(null);
  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  // Rows are a pure view transform over the flat display list — playback,
  // deletion, splitting etc. keep working off `layers`/`layersRef`.
  const rows = useMemo(() => groupLayersIntoRows(layers), [layers]);
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Label thumbnails that failed to render (broken/unauthorized URL, video
  // frame that can't paint) fall back to the type icon — the label must never
  // show an empty box. Keyed by id+url so replacing the file retries.
  const [failedThumbKeys, setFailedThumbKeys] = useState(() => new Set());
  const thumbKey = (layer) => `${layer.id}|${layer.thumbUrl}`;
  const markThumbFailed = (layer) =>
    setFailedThumbKeys((prev) => {
      const key = thumbKey(layer);
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  const [playing, setPlaying] = useState(false);
  const playingRef = useRef(false);

  const [mediaWaiting, setMediaWaitingState] = useState(false);
  const mediaWaitingRef = useRef(false);
  const setMediaWaiting = (v) => {
    mediaWaitingRef.current = v;
    setMediaWaitingState(v);
  };
  const narrationHoldersRef = useRef(narrationHolders);
  narrationHoldersRef.current = narrationHolders;
  const restartAnimationRef = useRef(false);
  // Bumped to rerun the scene start (a seek while playing).
  const [restartToken, setRestartToken] = useState(0);

  // Ghost mode (toolbar toggle): elements not visible at the playhead draw
  // faded instead of hidden.
  const [ghostMode, setGhostMode] = useState(false);
  const ghostModeRef = useRef(false);
  // triggerAnimationsAtTime is defined below; effects above reach it via ref.
  const triggerAnimationsAtTimeRef = useRef(null);
  const driveAutoVideosRef = useRef(null);
  const driveNarrationRef = useRef(driveNarration);
  driveNarrationRef.current = driveNarration;

  useEffect(() => {
    playingRef.current = playing;
    // Scene narration / animation engine only run at the preview rate while the
    // timeline drives them; everything else in the editor stays real-time.
    onPlaybackSpeedChangeRef.current?.(playing ? speedRef.current : 1);
    // Pre-hide interactive boxes when play starts so they only appear when the
    // gate explicitly activates them. Restore on stop/pause so authors can
    // see/edit them. Uses the ref so this effect never has stale-closure issues.
    interactiveClipsRef.current.forEach(({ boxId }) => {
      const el = document.getElementById(`box-${boxId}`);
      if (!el) return;
      if (playing) {
        el.style.opacity = "0";
        el.style.pointerEvents = "none";
      } else {
        el.style.opacity = "";
        el.style.pointerEvents = "";
      }
    });
    // Playback hid the ghosts; repaint them once at rest.
    if (!playing && ghostModeRef.current) {
      triggerAnimationsAtTimeRef.current?.(currentFrameRef.current / FPS, {
        shouldPlayMedia: false,
      });
    }
  }, [playing]);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [zoom, setZoom] = useState(1);
  // Playback rate for scene preview. Read through speedRef inside the interval
  // so a change never has to tear down and restart playback.
  const [speed, setSpeedState] = useState(readStoredSpeed);
  const speedRef = useRef(speed);
  const [selectedId, setSelectedId] = useState(selectedBoxId || null);
  const [thumbPreview, setThumbPreview] = useState(null); // { url, x, y }
  const [expandedBoxes, setExpandedBoxes] = useState(() => new Set());
  // Set while hovering a clip that has a splitGroup — every other clip
  // sharing that group (its split siblings) gets highlighted so it's clear
  // which pieces used to be one continuous audio/video/element.
  const [hoveredSplitGroup, setHoveredSplitGroup] = useState(null);
  // selectedSubField: { boxId, path, paths, anchor } — drives AnimationPanel
  // field-mode. Ctrl/Cmd/Shift+click on sub-rows selects several fields so one
  // preset applies to all of them (see utils/subFieldSelection).
  const [selectedSubField, setSelectedSubField] = useState(null);

  useEffect(() => {
    onSelectSubField?.(selectedSubField);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubField]);

  const toggleExpanded = (boxId) =>
    setExpandedBoxes((prev) => {
      const next = new Set(prev);
      if (next.has(boxId)) next.delete(boxId);
      else next.add(boxId);
      return next;
    });

  // Drop stale entries from expandedBoxes / selectedSubField when a box is removed.
  useEffect(() => {
    const liveIds = new Set(layers.map((l) => l.id));
    setExpandedBoxes((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (liveIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
    setSelectedSubField((prev) => (prev && !liveIds.has(prev.boxId) ? null : prev));
  }, [layers]);

  // Selecting a layer that is hidden/ghosted at the playhead seeks to the
  // frame where it becomes visible; visible elements never trigger a seek.
  const ensureSelectedLayerVisible = (boxId) => {
    if (!boxId || playingRef.current) return;
    const layer = layersRef.current.find((l) => l.id === boxId);
    if (!layer || typeof layer.startFrame !== "number") return;
    // Scene audio has no stage element — "visible" means the playhead is
    // inside its window. Outside it, jump to the sound's start.
    if (layer.type === "scene-audio") {
      if (layer.visible === false) return;
      const f = currentFrameRef.current;
      const inWindow =
        f >= layer.startFrame && (typeof layer.endFrame !== "number" || f <= layer.endFrame);
      if (!inWindow) seekToFrame(layer.startFrame);
      return;
    }
    const box = boxes?.[boxId];
    const timelineVisible =
      box?.timeline?.visible !== undefined ? box.timeline.visible : box?.visible;
    if (timelineVisible === false) return;
    // Outside its own window the element does not belong on stage — decide
    // that from the layer's timing, never from the DOM. The opacity that hides
    // a layer is written imperatively per playhead tick, so at selection time
    // it can still describe the PREVIOUS playhead position: selecting layer 2
    // from layer 1 then read "already visible", skipped the seek and showed
    // nothing until the author went to layer 3 and back.
    const frame = currentFrameRef.current;
    const outOfWindow =
      frame < layer.startFrame || (typeof layer.endFrame === "number" && frame > layer.endFrame);
    // Inside its window it can still be hidden (entrance not done yet / exit
    // already finished), which only the DOM knows. A ghosted element counts as
    // hidden — the faded copy is not "on stage" — and so does a missing one.
    const el = document.getElementById(`box-${boxId}`);
    const hiddenOnStage =
      !el ||
      el.style.opacity === "0" ||
      el.dataset.aideaGhost === "1" ||
      parseFloat(window.getComputedStyle(el).opacity || "1") === 0;
    if (!outOfWindow && !hiddenOnStage) return;
    const entryDurationSec = box?.boxAnimation?.duration ?? box?.textAnimation?.duration ?? 0;
    let target = layer.startFrame + Math.round(entryDurationSec * FPS);
    if (typeof layer.endFrame === "number" && target > layer.endFrame) {
      target = layer.startFrame;
    }
    seekToFrame(target);
  };

  // External selections (canvas click, LayersPanel) arrive via the
  // selectedBoxId prop; row clicks call ensureSelectedLayerVisible directly.
  useEffect(() => {
    setSelectedId(selectedBoxId || null);
    ensureSelectedLayerVisible(selectedBoxId);
  }, [selectedBoxId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sahne değişince player sıfırla ──────────────────────
  useEffect(() => {
    stopInterval();
    announceSeek();
    dropMediaWait({ newPass: true });
    // Clear any active interactive gate from the previous scene so stale
    // window.__ideaGateBoxId never bleeds into the newly loaded scene.
    delete window.__ideaGateBoxId;
    setPausedForInteraction(null);
    setPlaying(false);
    currentFrameRef.current = 0;
    clockRef.current.seek(0);
    setCurrentFrame(0);
    pauseAnimation?.();
    onAnimationReset?.();
    setCurrentTimeRef.current?.(0, { seek: true });
    userSetDurationRef.current = false; // allow auto-duration on scene change
    setExpandedBoxes(new Set());
    setSelectedSubField(null);

    // Sahne değiştiğinde tüm media elementlerini durdur
    const audioElements = document.querySelectorAll("#airlayer audio, #airlayer_cv audio");
    const videoElements = document.querySelectorAll("#airlayer video, #airlayer_cv video");
    audioElements.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
      learnerCue(audio, true); // a new scene: the preview owns its cues again
    });
    videoElements.forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
    stopAllYoutubeRef.current?.();
  }, [currentScene]); // eslint-disable-line react-hooks/exhaustive-deps

  const tracksAreaRef = useRef(null);
  const tracksElRef = useRef(null);
  const rafRef = useRef(null);
  const clockRef = useRef(null);
  if (!clockRef.current) clockRef.current = new SceneClock();
  const gateHandlersRef = useRef({});
  const gateRef = useRef(null);
  if (!gateRef.current) {
    gateRef.current = new MediaGate({
      listHolders: () => gateHandlersRef.current.list(),
      onClose: () => gateHandlersRef.current.close(),
      onOpen: () => gateHandlersRef.current.open(),
      onContextBlocked: () => gateHandlersRef.current.contextBlocked(),
      isRunning: () => clockRef.current.isRunning(),
      videoComfortMs: 0,
      audioWaitLimitMs: Infinity,
    });
  }
  useEffect(() => {
    const poke = () => gateRef.current.poke();
    const events = ["canplay", "canplaythrough", "seeked", "loadeddata", "error"];
    events.forEach((t) => document.addEventListener(t, poke, true));
    return () => {
      events.forEach((t) => document.removeEventListener(t, poke, true));
      gateRef.current.dispose();
    };
  }, []);
  const currentFrameRef = useRef(0);
  const rootRef = useRef(null);

  // Expose the playhead to action thunks (addBoxThunk/addSound) so new
  // elements are inserted at the playhead instead of frame 0.
  const currentSceneRef = useRef(currentScene);
  currentSceneRef.current = currentScene;
  // Explicit playhead moves, counted for surfaces that must tell a seek from
  // the update that settles a stopped clock (animated images).
  const seekIdRef = useRef(0);
  const announceSeek = () => {
    seekIdRef.current += 1;
    document.dispatchEvent(
      new CustomEvent("aidea-timeline-seek", { detail: { seekId: seekIdRef.current } })
    );
  };
  useEffect(() => {
    const get = () => ({
      frame: currentFrameRef.current,
      sceneId: currentSceneRef.current,
      playing: playingRef.current,
      advancing: playingRef.current && !pausedForInteractionRef.current && !mediaWaitingRef.current,
      seekId: seekIdRef.current,
    });
    registerPlayheadTracker(get);
    return () => unregisterPlayheadTracker(get);
  }, []);

  // keep latest callbacks in refs so interval closure doesn't go stale
  const onTriggerLayerAnimationRef = useRef(onTriggerLayerAnimation);
  const onTriggerAnimationRef = useRef(onTriggerAnimation);
  const setCurrentTimeRef = useRef(setCurrentTime);
  useEffect(() => {
    onTriggerLayerAnimationRef.current = onTriggerLayerAnimation;
  }, [onTriggerLayerAnimation]);
  useEffect(() => {
    onTriggerAnimationRef.current = onTriggerAnimation;
  }, [onTriggerAnimation]);
  useEffect(() => {
    setCurrentTimeRef.current = setCurrentTime;
  }, [setCurrentTime]);

  const onPlaybackSpeedChangeRef = useRef(onPlaybackSpeedChange);
  useEffect(() => {
    onPlaybackSpeedChangeRef.current = onPlaybackSpeedChange;
  }, [onPlaybackSpeedChange]);

  const changeSpeed = (next) => {
    if (!SPEED_PRESETS.includes(next) || next === speedRef.current) return;
    speedRef.current = next;
    setSpeedState(next);
    try {
      localStorage.setItem(SPEED_STORAGE_KEY, String(next));
    } catch {
      /* private mode — the speed simply won't persist */
    }

    clockRef.current.setRate(next);
    if (playingRef.current) onPlaybackSpeedChangeRef.current?.(next);
  };

  // A YouTube layer caps the preview rate — the iframe can't be driven faster
  // than 2x, and letting the playhead outrun it would just desync the scene.
  const hasYoutubeLayer = useMemo(
    () => layers.some((l) => l.type === "video" && isYoutubeUrl(l.mediaUrl)),
    [layers]
  );
  const maxSpeed = hasYoutubeLayer ? YOUTUBE_MAX_SPEED : null;

  useEffect(() => {
    if (maxSpeed && speedRef.current > maxSpeed) changeSpeed(maxSpeed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxSpeed]);

  const pps = PIXELS_PER_SECOND * zoom;
  // The marker (duration) is user-owned and may be SHORTER than the content —
  // ruler, playback and seeking always cover the full span of both.
  const contentEndFrame = layers.reduce((m, l) => Math.max(m, l.endFrame || 0), 0);
  const timelineSpan = Math.max(duration, contentEndFrame);
  const timelineSpanRef = useRef(timelineSpan);
  timelineSpanRef.current = timelineSpan;
  const totalWidth = Math.max(800, (timelineSpan / FPS) * pps + TRACK_END_GUTTER);
  const frameToX = (f) => (f / FPS) * pps;
  const xToFrame = (x) => Math.round((x / pps) * FPS);

  // ── Fit-to-scene zoom ────────────────────────────────────
  // On mount, on expand and on scene switch the zoom follows the visible width
  // so the whole scene span fits on screen. Layers build async (and media
  // durations fill in later), so fit stays live until the user takes over —
  // a manual zoom or any pointer interaction inside the tracks area.
  const autoFitRef = useRef(true);
  const fitToScene = () => {
    const el = tracksAreaRef.current;
    const spanSec = timelineSpanRef.current / FPS;
    if (!el || !spanSec) return;
    const available = el.clientWidth - LABEL_WIDTH - TRACK_END_GUTTER;
    if (available <= 0) return;
    const fit = available / (spanSec * PIXELS_PER_SECOND);
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.floor(fit * 1000) / 1000));
    setZoom((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    el.scrollLeft = 0;
  };
  const fitToSceneRef = useRef(fitToScene);
  fitToSceneRef.current = fitToScene;
  const setManualZoom = (z) => {
    autoFitRef.current = false;
    setZoom(z);
  };

  useEffect(() => {
    if (!collapsed) autoFitRef.current = true;
  }, [collapsed, currentScene]);

  useEffect(() => {
    if (!collapsed && autoFitRef.current) fitToSceneRef.current();
  }, [collapsed, currentScene, timelineSpan]);

  useEffect(() => {
    const el = tracksAreaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      if (autoFitRef.current) fitToSceneRef.current();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // YouTube renders an <iframe>, so the DOM-driven media sync below cannot
  // reach it — `#box-<id> video` matches nothing and the layer would simply
  // never play in preview. Mirror the same play/seek decision over an event
  // channel that EnrichedPlayerPluginEditor listens to.
  const syncYoutubeLayer = (layer, currentTime, inRange, shouldPlayMedia) => {
    const trimStartSec = (layer.trimStart ?? 0) / FPS;
    let playing = false;

    if (layer.autoPlay) {
      const playStartSec = (layer.playStartFrame ?? layer.startFrame) / FPS;
      const trimEndFrames =
        typeof layer.trimEnd === "number" ? layer.trimEnd : layer.mediaDurationFrames;
      const trimEndSec = typeof trimEndFrames === "number" ? trimEndFrames / FPS : null;
      const mediaTargetSec = trimStartSec + (currentTime - playStartSec);
      const beforeEnd = trimEndSec === null || mediaTargetSec < trimEndSec;
      playing = shouldPlayMedia && inRange && currentTime >= playStartSec && beforeEnd;
    } else if (inRange) {
      // Manual videos are viewer-started — the timeline only cuts them when the
      // clip leaves the stage, it never starts them.
      return;
    }

    document.dispatchEvent(
      new CustomEvent("aidea-timeline-youtube-sync", {
        detail: {
          boxId: layer.id,
          playing,
          rate: playing ? speedRef.current : 1,
          resetSec: trimStartSec,
        },
      })
    );
  };

  // Bulk `#airlayer video` pauses don't reach iframe players either — this is
  // the YouTube equivalent, for the paths that stop everything at once.
  const stopAllYoutube = () => {
    layersRef.current.forEach((layer) => {
      if (layer.type !== "video" || !isYoutubeUrl(layer.mediaUrl)) return;
      document.dispatchEvent(
        new CustomEvent("aidea-timeline-youtube-sync", {
          detail: {
            boxId: layer.id,
            playing: false,
            rate: 1,
            resetSec: (layer.trimStart ?? 0) / FPS,
          },
        })
      );
    });
  };
  const stopAllYoutubeRef = useRef(null);
  stopAllYoutubeRef.current = stopAllYoutube;

  // Off-stage videos wait on their in-point;
  const seekIfOff = (el, sec) => {
    if (!el.seeking && Math.abs(el.currentTime - sec) > 0.05) el.currentTime = sec;
  };

  // autoPlay videos follow the scene clock through the shared sync module
  // (same as the Visor).
  const videoSyncRef = useRef(new Map());
  const driveAutoVideo = (layer, el, targetSec, endSec) => {
    let sync = videoSyncRef.current.get(layer.id);
    if (!sync) videoSyncRef.current.set(layer.id, (sync = createVideoSyncState()));
    driveVideoSync(sync, el, targetSec, speedRef.current, endSec);
  };
  // Where an autoPlay video should be at scene time `t` (seconds).
  const autoVideoPlan = (layer, t) => {
    const playStartSec = (layer.playStartFrame ?? layer.startFrame) / FPS;
    const trimStartSec = (layer.trimStart ?? 0) / FPS;
    const trimEndFrames =
      typeof layer.trimEnd === "number" ? layer.trimEnd : layer.mediaDurationFrames;
    const trimEndSec = typeof trimEndFrames === "number" ? trimEndFrames / FPS : null;
    return { playStartSec, trimStartSec, trimEndSec, targetSec: trimStartSec + (t - playStartSec) };
  };
  // Called on every screen frame while playing.
  const driveAutoVideos = (t) => {
    layersRef.current.forEach((layer) => {
      if (!layer.visible || layer.type !== "video" || !layer.autoPlay) return;
      const layerStart = (layer.startFrame || 0) / FPS;
      const layerEnd = (layer.endFrame || timelineSpanRef.current) / FPS;
      if (t < layerStart || t > layerEnd) return;
      const { playStartSec, trimStartSec, trimEndSec, targetSec } = autoVideoPlan(layer, t);
      if (t < playStartSec) return;
      const el = document.querySelector(`#box-${layer.id} video`);
      if (!el) return;
      if (trimEndSec !== null && targetSec >= trimEndSec) {
        const period = trimEndSec - trimStartSec;
        if (!layer.loop || !(period > 0)) return;
        driveAutoVideo(layer, el, trimStartSec + ((targetSec - trimStartSec) % period), trimEndSec);
      } else {
        driveAutoVideo(layer, el, targetSec, trimEndSec);
      }
    });
  };
  // Legacy sound-list cues run their own clock and are not waited for.
  const dueMediaHolders = () => {
    const t = clockRef.current.positionSec();
    // Due from the frame shown, the one an interaction stops on.
    const shownSec = secToFrame(t) / FPS;
    const out = [];
    layersRef.current.forEach((layer) => {
      if (!layer.visible) return;
      const start = (layer.startFrame || 0) / FPS;
      const end = (layer.endFrame || timelineSpanRef.current) / FPS;
      if (shownSec < start || t > end) return;
      if (layer.type === "video" && layer.autoPlay) {
        const { playStartSec, trimEndSec, targetSec } = autoVideoPlan(layer, t);
        if (shownSec < playStartSec) return;
        if (trimEndSec !== null && targetSec >= trimEndSec && !layer.loop) return;
        out.push(
          elementHolder(layer.id, "video", () => document.querySelector(`#box-${layer.id} video`), {
            startSec: playStartSec,
          })
        );
      } else if (layer.type === "audio") {
        if (layer.manualCue) return; // waits for the learner, not the other way round
        const list = pluginToolbarsRef.current[layer.id]?.state?.soundList;
        if (Array.isArray(list) && list.length > 0) return;
        const el = document.querySelector(`#box-${layer.id} audio`);
        if (el && el.dataset.learnerCue === "1") return; // the preview does not wait for it
        out.push(
          elementHolder(layer.id, "audio", () => document.querySelector(`#box-${layer.id} audio`), {
            startSec: start,
          })
        );
      }
    });
    const narration = narrationHoldersRef.current?.(shownSec);
    if (narration) out.push(...narration);
    return out;
  };

  // Like a pause, but the play intent is kept.
  const mediaGateClose = () => {
    stopInterval();
    pauseAnimationRef.current?.();
    restartAnimationRef.current = true;
    document.querySelectorAll("#airlayer audio, #airlayer_cv audio").forEach((a) => {
      if (a.dataset.learnerCue !== "1") a.pause();
    });
    document.querySelectorAll("#airlayer video, #airlayer_cv video").forEach((v) => v.pause());
    stopAllYoutubeRef.current?.();
    setMediaWaiting(true);
  };

  // Back to the earliest boundary, at most one frame before the shown frame.
  const entryBoundarySec = (entering, nowSec) => {
    const starts = entering.map((h) => h.startSec).filter((x) => typeof x === "number");
    const floor = currentFrameRef.current / FPS - 1 / FPS;
    return Math.min(nowSec, Math.max(floor, starts.length ? Math.min(...starts) : nowSec));
  };
  const entryBoundarySecRef = useRef(entryBoundarySec);
  entryBoundarySecRef.current = entryBoundarySec;
  const holdAtBoundary = (entering, nowSec) => {
    const sec = entryBoundarySec(entering, nowSec);
    const frame = secToFrame(sec);
    clockRef.current.seek(sec);
    currentFrameRef.current = frame;
    setCurrentFrame(frame);
    triggerAnimationsAtTimeRef.current(sec, { shouldPlayMedia: false });
    setCurrentTimeRef.current?.(sec);
  };
  const holdAtBoundaryRef = useRef(holdAtBoundary);
  holdAtBoundaryRef.current = holdAtBoundary;

  // `keepRestart`: a seek while playing; what the wait paused restarts.
  const dropMediaWait = ({ newPass = false, keepRestart = false } = {}) => {
    if (newPass) gateRef.current.resetPass();
    else gateRef.current.clear();
    if (!keepRestart) restartAnimationRef.current = false;
    if (mediaWaitingRef.current) setMediaWaiting(false);
  };

  gateHandlersRef.current = {
    list: dueMediaHolders,
    close: mediaGateClose,
    open: () => setMediaWaiting(false),
    // Context still down: back to the play control, whose press wakes it.
    contextBlocked: () => pauseNowRef.current?.(),
  };

  const audioSyncRef = useRef(new Map());
  const dropVideoSync = (keep) => {
    videoSyncRef.current.forEach((sync, id) => {
      if (keep && keep.has(id)) return;
      releaseVideoSync(sync);
      videoSyncRef.current.delete(id);
    });
    audioSyncRef.current.forEach((sync, id) => {
      if (!keep || !keep.has(id)) audioSyncRef.current.delete(id);
    });
  };
  // Scene change or unmount: drop every record (they hold the <video>).
  useEffect(() => () => dropVideoSync(), [currentScene]);

  // A removed layer: drop its record.
  useEffect(() => {
    dropVideoSync(new Set(layers.map((l) => l.id)));
  }, [layers]);

  // ── triggerAnimationsAtTime — mirrors RemotionTimeline ───
  // `seek`: the playhead was moved (scrub, stop), as opposed to playing on.
  const triggerAnimationsAtTime = (currentTime, { shouldPlayMedia = true, seek = false } = {}) => {
    const layerList = layersRef.current;
    // Ghosts only at rest — playback shows the real playhead truth.
    const ghost = ghostModeRef.current && !playingRef.current;
    layerList.forEach((layer) => {
      if (!layer.visible) return;

      const layerStart = (layer.startFrame || 0) / FPS;
      const layerEnd = (layer.endFrame || timelineSpan) / FPS;
      const inRange = currentTime >= layerStart && currentTime <= layerEnd;

      // Media layer kontrolü - timeline play olduğunda audio/video otomatik oynatılsın
      if (MEDIA_TYPES.has(layer.type)) {
        let mediaElement = null;

        if (layer.type === "scene-audio") {
          // EditorApp setCurrentTime callback'i scene-audio'yu zaten yönetiyor
          // IdeaTimeline burada müdahale etmemeli — atla
          return;
        } else {
          const mediaSelector =
            layer.type === "video" ? `#box-${layer.id} video` : `#box-${layer.id} audio`;
          mediaElement = document.querySelector(mediaSelector);
        }

        if (mediaElement) {
          const rate = shouldPlayMedia ? speedRef.current : 1;
          // A playing autoPlay video's rate belongs to the sync module (nudges).
          const synced = layer.type === "video" && layer.autoPlay && shouldPlayMedia;
          if (!synced && mediaElement.playbackRate !== rate) mediaElement.playbackRate = rate;

          if (layer.type === "video" && layer.autoPlay) {
            // autoPlay video: stage presence (inRange) governs visibility; the
            // play window [playStart, playStart+playLen] governs playback. Freeze
            // on the first frame before the window and the last frame after it.
            const {
              playStartSec,
              trimStartSec,
              trimEndSec,
              targetSec: mediaTargetSec,
            } = autoVideoPlan(layer, currentTime);
            // Exact clock time while playing.
            const fineSec = clockRef.current.isRunning()
              ? clockRef.current.positionSec()
              : currentTime;
            const fineTargetSec = mediaTargetSec + (fineSec - currentTime);

            if (!inRange) {
              if (!mediaElement.paused) mediaElement.pause();
              seekIfOff(mediaElement, trimStartSec);
            } else if (currentTime < playStartSec) {
              if (!mediaElement.paused) mediaElement.pause();
              if (Math.abs(mediaElement.currentTime - trimStartSec) > 0.1)
                mediaElement.currentTime = trimStartSec;
            } else if (trimEndSec !== null && mediaTargetSec >= trimEndSec) {
              const loopPeriodSec = trimEndSec - trimStartSec;
              if (layer.loop && loopPeriodSec > 0) {
                // loop restarts media within the clip's timeline range: wrap
                // the trim-relative target time instead of pausing at the
                // media's own end (native <video loop> can't do this itself —
                // this branch imperatively re-seeks/pauses every tick, which
                // overrides it).
                const wrappedSec = trimStartSec + ((mediaTargetSec - trimStartSec) % loopPeriodSec);
                if (shouldPlayMedia) {
                  driveAutoVideo(
                    layer,
                    mediaElement,
                    trimStartSec + ((fineTargetSec - trimStartSec) % loopPeriodSec),
                    trimEndSec
                  );
                } else {
                  if (Math.abs(mediaElement.currentTime - wrappedSec) > 0.5)
                    mediaElement.currentTime = wrappedSec;
                  if (!mediaElement.paused) mediaElement.pause();
                }
              } else {
                if (!mediaElement.paused) mediaElement.pause();
                if (mediaElement.currentTime < trimEndSec - 0.05)
                  mediaElement.currentTime = trimEndSec;
              }
            } else if (shouldPlayMedia) {
              driveAutoVideo(layer, mediaElement, fineTargetSec, trimEndSec);
            } else {
              if (Math.abs(mediaElement.currentTime - mediaTargetSec) > 0.5)
                mediaElement.currentTime = mediaTargetSec;
              if (!mediaElement.paused) mediaElement.pause();
            }
          } else if (layer.type === "video") {
            if (!inRange) {
              if (!mediaElement.paused) mediaElement.pause();
              seekIfOff(mediaElement, (layer.trimStart ?? 0) / FPS);
            }
          } else if (layer.manualCue) {
            if (!inRange) {
              if (!mediaElement.paused) mediaElement.pause();
              seekIfOff(mediaElement, (layer.trimStart ?? 0) / FPS);
            }
          } else if (learnerCue(mediaElement, seek)) {
            // The learner's cue: the preview neither starts, stops nor moves it.
            if (shouldPlayMedia && mediaElement.dataset.pausedByPreview === "1") {
              delete mediaElement.dataset.pausedByPreview;
              mediaElement.play().catch(() => {});
            }
          } else {
            // Audio starts with the scene through the shared sync (same as the
            // Visor). Only a moved playhead or leaving the clip moves it; a
            // pause keeps its position.
            const trimStartSec = (layer.trimStart ?? 0) / FPS;
            let sync = audioSyncRef.current.get(layer.id);
            if (!sync) audioSyncRef.current.set(layer.id, (sync = createAudioSyncState()));
            if (seek && inRange) {
              seekAudioSync(sync, mediaElement, trimStartSec + (currentTime - layerStart));
            }
            if (inRange && shouldPlayMedia) {
              const fineSec = clockRef.current.isRunning()
                ? clockRef.current.positionSec()
                : currentTime;
              driveAudioSync(
                sync,
                mediaElement,
                trimStartSec + (fineSec - layerStart),
                speedRef.current,
                null,
                trimStartSec
              );
            } else {
              if (!mediaElement.paused) mediaElement.pause();
              if (!inRange && (seek || sync.placed)) {
                mediaElement.currentTime = trimStartSec;
                resetAudioSync(sync);
              }
            }
          }
        } else if (layer.type === "video" && isYoutubeUrl(layer.mediaUrl)) {
          syncYoutubeLayer(layer, currentTime, inRange, shouldPlayMedia);
        }
      }

      if (!inRange) {
        onTriggerLayerAnimationRef.current?.(layer.id, currentTime, {
          isTimeUpdate: true,
          inRange: false,
          ghost,
          layerRelativeTime: 0,
        });
        return;
      }

      const relativeTime = currentTime - layerStart;

      if (layer.hasAnimations && layer.animations?.length >= 2) {
        for (let i = 0; i < layer.animations.length - 1; i++) {
          const kf1 = layer.animations[i];
          const kf2 = layer.animations[i + 1];
          if (!kf1 || !kf2) continue;
          const t1 = kf1.startTime ?? kf1.duration ?? i;
          const t2 = kf2.startTime ?? kf2.duration ?? i + 1;
          if (relativeTime >= t1 && relativeTime <= t2) {
            const seg = t2 - t1;
            const prog = seg > 0 ? Math.min(1, Math.max(0, (relativeTime - t1) / seg)) : 0;
            onTriggerLayerAnimationRef.current?.(layer.id, currentTime, {
              animation: kf1,
              progress: prog,
              duration: seg,
              isActive: true,
              animationIndex: i,
              layerRelativeTime: relativeTime,
              startKeyframe: kf1,
              endKeyframe: kf2,
            });
            break;
          }
        }
      } else if (layer.hasAnimations && layer.animations?.length === 1) {
        const kf = layer.animations[0];
        const t0 = kf.startTime ?? kf.duration ?? 0;
        if (relativeTime >= t0) {
          onTriggerLayerAnimationRef.current?.(layer.id, currentTime, {
            animation: kf,
            progress: 1,
            duration: 0,
            isActive: true,
            animationIndex: 0,
            layerRelativeTime: relativeTime,
            startKeyframe: kf,
            endKeyframe: kf,
          });
        }
      }

      onTriggerLayerAnimationRef.current?.(layer.id, currentTime, {
        isTimeUpdate: true,
        inRange: true,
        ghost,
        layerRelativeTime: relativeTime,
        layerStartFrame: layer.startFrame || 0,
        layerEndFrame: layer.endFrame || timelineSpan,
      });
    });
  };
  triggerAnimationsAtTimeRef.current = triggerAnimationsAtTime;
  driveAutoVideosRef.current = driveAutoVideos;

  // Apply playhead-gated visibility whenever the layer set rebuilds (scene
  // open, box add/remove, clip drag). Playback ticks already do this.
  useEffect(() => {
    if (playingRef.current) return;
    if (!layers.length) return;
    triggerAnimationsAtTime(currentFrameRef.current / FPS, { shouldPlayMedia: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers]);

  const handleGhostToggle = () => {
    const next = !ghostModeRef.current;
    ghostModeRef.current = next;
    setGhostMode(next);
    if (!playingRef.current) {
      triggerAnimationsAtTime(currentFrameRef.current / FPS, { shouldPlayMedia: false });
    }
  };

  // ── Playback ─────────────────────────────────────────────
  const stopInterval = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    clockRef.current.pause();
  };

  // Interactive gate: pause playhead when it reaches an InteractivePlugin
  // box's startFrame; resume when the interaction signals completion via
  // "aidea-interactive-complete" (or the legacy "aidea-scene-advance").
  // State shape: null | { boxId, startFrame, endFrame }
  //
  // BUG-9 fix: the old code called stopInterval() inside the setInterval
  // callback without changing `playing`, so useEffect[playing] never
  // re-ran and the interval stayed dead after the interactive completed.
  // Fix: make the interval useEffect also depend on `pausedForInteraction`
  // so clearing it (→ null) causes useEffect to re-run → new interval.
  const [pausedForInteraction, setPausedForInteraction] = useState(null);
  const pausedForInteractionRef = useRef(null);
  useEffect(() => {
    pausedForInteractionRef.current = pausedForInteraction;
  }, [pausedForInteraction]);

  // Build an array of InteractivePlugin clips on the current scene.
  // Derived from `layers` (which already ran resolveBoxTiming — handles the
  // case where box.timeline is absent and defaults startFrame to 0) rather
  // than reading box.timeline.startFrame directly (which would be undefined
  // for freshly seeded boxes, causing the gate to never trigger).
  const interactiveClips = useMemo(() => {
    return layers
      .filter((l) => {
        const tb = pluginToolbars[l.id];
        return tb && tb.pluginId === "InteractivePlugin";
      })
      .map((l) => ({
        boxId: l.id,
        startFrame: typeof l.startFrame === "number" ? l.startFrame : 0,
        endFrame: l.endFrame,
      }));
  }, [layers, pluginToolbars]);

  // Keep a stable ref so side-effect callbacks always see the latest clips
  // without needing to be listed as dependencies.
  const interactiveClipsRef = useRef(interactiveClips);
  useEffect(() => {
    interactiveClipsRef.current = interactiveClips;
  }, [interactiveClips]);

  // Shared resume handler — advances the playhead past the interactive
  // clip's endFrame and clears the gate. Clearing `pausedForInteraction`
  // triggers useEffect[playing, pausedForInteraction] to restart the
  // interval (the BUG-9 fix: previously only `playing` was in deps, but
  // `playing` never changed so the effect never re-ran after stopInterval).
  const resumeAfterInteraction = useRef(null);
  resumeAfterInteraction.current = () => {
    const paused = pausedForInteractionRef.current;
    if (!paused) return;

    // Clear test-mode flag before playAnimation triggers a canvas re-render.
    delete window.__ideaGateBoxId;

    const resumeFrame = paused.startFrame + 1;

    // Hide the interactive box.
    const el = document.getElementById(`box-${paused.boxId}`);
    if (el) {
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
    }

    // Resume audio/video and animation that were paused when the gate activated.
    // triggerAnimationsAtTime will re-evaluate every layer's inRange state so
    // media that should be playing at resumeFrame starts again.
    playAnimation?.();
    dropMediaWait();
    triggerAnimationsAtTime(resumeFrame / FPS, { shouldPlayMedia: false, seek: true });
    setCurrentTimeRef.current?.(resumeFrame / FPS, { seek: true });

    currentFrameRef.current = resumeFrame;
    clockRef.current.seek(resumeFrame / FPS);
    setCurrentFrame(resumeFrame);
    setPausedForInteraction(null); // ← triggers useEffect restart → new interval
  };

  useEffect(() => {
    // "aidea-interactive-complete" is the canonical signal (new system).
    // "aidea-scene-advance" is the legacy signal (EndScreenItem → IdeaTimeline).
    // Both trigger the same resume logic so old content keeps working.
    const onComplete = () => resumeAfterInteraction.current?.();
    document.addEventListener("aidea-interactive-complete", onComplete);
    document.addEventListener("aidea-scene-advance", onComplete);
    return () => {
      document.removeEventListener("aidea-interactive-complete", onComplete);
      document.removeEventListener("aidea-scene-advance", onComplete);
    };
  }, []);

  // Stable refs so play-command handlers always call the latest callbacks.
  const playAnimationRef = useRef(playAnimation);
  const pauseAnimationRef = useRef(pauseAnimation);
  const onAnimationResetRef = useRef(onAnimationReset);
  useEffect(() => {
    playAnimationRef.current = playAnimation;
  }, [playAnimation]);
  useEffect(() => {
    pauseAnimationRef.current = pauseAnimation;
  }, [pauseAnimation]);
  useEffect(() => {
    onAnimationResetRef.current = onAnimationReset;
  }, [onAnimationReset]);

  // Broadcast playing state so external surfaces (CanvasWorkflowPanel) can
  // mirror a play/pause icon without coupling to this component's local state.
  // `advancing`: the clock actually runs.
  useEffect(() => {
    const advancing = playing && !pausedForInteraction && !mediaWaiting;
    document.dispatchEvent(
      new CustomEvent("aidea-timeline-status", { detail: { playing, advancing } })
    );
  }, [playing, pausedForInteraction, mediaWaiting]);

  // Accept play/stop commands from the workflow panel (and any future surface).
  // "aidea-timeline-play" toggles play/pause; "aidea-timeline-stop" calls handleStop.
  // Using refs so the handlers never go stale across re-renders.
  const handleStopRef = useRef(null);
  const pauseAtEndRef = useRef(null);
  const rewindIfEndedRef = useRef(null);
  // Frame where play last auto-stopped at the end (null = not parked there).
  const endedAtFrameRef = useRef(null);
  useEffect(() => {
    const onPlay = () => {
      if (!playingRef.current) {
        unlockAudioGraph();
        rewindIfEndedRef.current?.();
        onAnimationResetRef.current?.();
        // Repaint in the same task — the reset made everything visible and the
        // first tick is a frame away. playingRef pre-set so ghosts stay off.
        playingRef.current = true;
        triggerAnimationsAtTimeRef.current?.(currentFrameRef.current / FPS, {
          shouldPlayMedia: false,
        });
        setPlaying(true);
        playAnimationRef.current?.();
      } else {
        delete window.__ideaGateBoxId;
        pauseNowRef.current();
      }
    };
    const onStop = () => handleStopRef.current?.();
    document.addEventListener("aidea-timeline-play", onPlay);
    document.addEventListener("aidea-timeline-stop", onStop);
    return () => {
      document.removeEventListener("aidea-timeline-play", onPlay);
      document.removeEventListener("aidea-timeline-stop", onStop);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // BUG-9 fix: `pausedForInteraction` is now a dependency. When the gate
    // clears (null), this effect re-runs with playing=true and no gate →
    // the clock loop restarts automatically.
    if (playing && !pausedForInteraction && !mediaWaiting) {
      const clock = clockRef.current;
      if (!gateRef.current.check()) return undefined;
      if (restartAnimationRef.current) {
        restartAnimationRef.current = false;
        playAnimationRef.current?.();
      }
      clock.setRate(speedRef.current);
      // Goes on from where it stopped: whatever moves the playhead seeks the
      // clock itself, so a start never rewinds it to the last drawn frame.
      clock.start();
      // Narration starts in this task, not on the first frame change; the
      // frame updates that follow find it playing. Not when an interactive
      // clip sits on this frame: the first frame change stops the scene there.
      const startFrame = currentFrameRef.current;
      const gatedHere = interactiveClipsRef.current.some((c) => c.startFrame === startFrame);
      if (!gatedHere) driveNarrationRef.current?.(startFrame / FPS);
      // The loop outlives re-renders (it must not restart while playing), so
      // it reaches everything render-scoped through refs.
      const tick = () => {
        rafRef.current = requestAnimationFrame(tick);
        const nowSec = clock.positionSec();
        // Earliest boundary wins; media before an interaction on the same frame.
        const entering = gateRef.current.pendingEntries();
        if (entering.length) {
          const prev = currentFrameRef.current;
          const reach = secToFrame(nowSec);
          const mediaSec = entryBoundarySecRef.current(entering, nowSec);
          const clip = interactiveClipsRef.current.find(
            (c) => reach >= c.startFrame && prev <= c.startFrame
          );
          const endFrame =
            prev > durationRef.current ? timelineSpanRef.current : durationRef.current;
          const earlier =
            (clip && clip.startFrame / FPS < mediaSec) ||
            (reach > endFrame && endFrame / FPS < mediaSec);
          if (!earlier) {
            gateRef.current.enter();
            holdAtBoundaryRef.current(entering, nowSec);
            return;
          }
        } else {
          gateRef.current.enter();
        }
        driveAutoVideosRef.current(nowSec);
        const next = secToFrame(nowSec);
        if (next === currentFrameRef.current) return;

        // Play stops at the scene end (the duration marker) — that IS the
        // scene's end even when content bars extend beyond it. Content past
        // the marker stays reachable by dragging the playhead manually; if
        // play starts out there, run to the span end instead of stopping
        // on the first tick.
        const sceneEndFrame = durationRef.current;
        const stopFrame =
          currentFrameRef.current > sceneEndFrame ? timelineSpanRef.current : sceneEndFrame;
        if (next > stopFrame) {
          // Hold the playhead at the end instead of rewinding to frame 0.
          pauseAtEndRef.current?.(stopFrame);
          return;
        }

        // Detect crossing into an InteractivePlugin clip.
        // We check "range-crossing" rather than exact-frame match so that:
        //   a) startFrame=0 clips are caught (prevFrame=0 <= 0, next=1 >= 0)
        //   b) clips with no box.timeline (resolveBoxTiming defaults to 0)
        //      are also caught
        //   c) seeking past startFrame still triggers the gate
        //
        // Crossing condition: next >= startFrame AND prevFrame <= startFrame
        // (equivalent to: the previous frame was before the clip, the next
        //  frame is at or inside the clip).
        const prevFrame = currentFrameRef.current;
        const ipHit = interactiveClipsRef.current.find(
          (clip) => next >= clip.startFrame && prevFrame <= clip.startFrame
        );
        if (ipHit) {
          stopInterval();
          const snapFrame = ipHit.startFrame;
          currentFrameRef.current = snapFrame;
          clock.seek(snapFrame / FPS);
          setCurrentFrame(snapFrame);

          // Mark this box as the active test-mode gate so InteractivePluginRenderer
          // can switch editing=false and allow real interaction in the editor.
          window.__ideaGateBoxId = ipHit.boxId;

          // Pause all audio/video so nothing plays in the background while
          // the learner is working through the interactive element.
          document
            .querySelectorAll("#airlayer audio, #airlayer_cv audio")
            .forEach((a) => a.pause());
          document
            .querySelectorAll("#airlayer video, #airlayer_cv video")
            .forEach((v) => v.pause());
          pauseAnimationRef.current?.(); // triggers EditorApp.setState → canvas re-renders → renderer picks up gate flag

          // Update all layer visibility at the gate frame so no layer stays
          // in a stale hidden/shown state from the previous tick.
          triggerAnimationsAtTimeRef.current(snapFrame / FPS, { shouldPlayMedia: false });

          // Make the interactive box visible and clickable (pre-hidden at play start).
          const el = document.getElementById(`box-${ipHit.boxId}`);
          if (el) {
            el.style.opacity = "";
            el.style.display = "";
            el.style.visibility = "";
            el.style.pointerEvents = "";
          }

          setPausedForInteraction({
            boxId: ipHit.boxId,
            startFrame: snapFrame,
            endFrame: ipHit.endFrame,
          });
          return;
        }

        currentFrameRef.current = next;
        const t = next / FPS;
        triggerAnimationsAtTimeRef.current(t);
        // Media sync runs now; the React re-renders may yield to the next frame.
        startTransition(() => {
          setCurrentFrame(next);
          setCurrentTimeRef.current?.(t);
        });
      };
      rafRef.current = requestAnimationFrame(tick);
    } else {
      stopInterval();
    }
    return stopInterval;
  }, [playing, pausedForInteraction, mediaWaiting, restartToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also clears an active interactive gate so it doesn't block the next Play.
  const pauseNow = () => {
    if (pausedForInteractionRef.current) {
      const gatedBoxId = pausedForInteractionRef.current.boxId;
      const el = document.getElementById(`box-${gatedBoxId}`);
      if (el) {
        el.style.opacity = "";
        el.style.pointerEvents = "";
      }
      setPausedForInteraction(null);
    }
    stopInterval();
    dropMediaWait();
    playingRef.current = false;
    setPlaying(false);
    pauseAnimationRef.current?.();
    document.querySelectorAll("#airlayer audio, #airlayer_cv audio").forEach((a) => {
      if (a.dataset.learnerCue === "1" && !a.paused) a.dataset.pausedByPreview = "1";
      a.pause();
    });
    document.querySelectorAll("#airlayer video, #airlayer_cv video").forEach((v) => v.pause());
    stopAllYoutubeRef.current?.();
  };
  const pauseNowRef = useRef(pauseNow);
  pauseNowRef.current = pauseNow;

  // ── Seek ─────────────────────────────────────────────────
  const seekToFrame = (frame) => {
    announceSeek();
    const clamped = Math.max(0, Math.min(timelineSpanRef.current, frame));
    currentFrameRef.current = clamped;
    clockRef.current.seek(clamped / FPS);
    setCurrentFrame(clamped);
    // Seeking always clears the interactive gate — the learner/author is
    // explicitly changing the playhead position, so the gate should not
    // block playback at the previous interactive startFrame.
    delete window.__ideaGateBoxId;
    if (pausedForInteractionRef.current) {
      // Restore interactive box visibility (it was hidden by the gate).
      const gatedBoxId = pausedForInteractionRef.current.boxId;
      const el = document.getElementById(`box-${gatedBoxId}`);
      if (el) {
        el.style.opacity = "";
        el.style.pointerEvents = "";
      }
      setPausedForInteraction(null);
    }
    // Playing: stop, position everything, then rerun the start so the gate checks the target.
    const playing = playingRef.current;
    if (playing) {
      stopInterval();
      pauseAnimationRef.current?.();
      restartAnimationRef.current = true;
    }
    dropMediaWait({ keepRestart: playing });
    triggerAnimationsAtTime(clamped / FPS, { shouldPlayMedia: false, seek: true });
    setCurrentTimeRef.current?.(clamped / FPS, { seek: true });
    if (playing) setRestartToken((n) => n + 1);
  };

  const handleStop = () => {
    stopInterval();
    announceSeek();
    dropMediaWait({ newPass: true });
    delete window.__ideaGateBoxId;
    // Clear gate and restore box visibility before resetting play state.
    if (pausedForInteractionRef.current) {
      const gatedBoxId = pausedForInteractionRef.current.boxId;
      const el = document.getElementById(`box-${gatedBoxId}`);
      if (el) {
        el.style.opacity = "";
        el.style.pointerEvents = "";
      }
    }
    setPausedForInteraction(null);
    setPlaying(false);
    currentFrameRef.current = 0;
    clockRef.current.seek(0);
    setCurrentFrame(0);
    pauseAnimation?.();
    onAnimationReset?.();
    document.querySelectorAll("#airlayer audio, #airlayer_cv audio").forEach((a) => {
      a.pause();
      a.currentTime = 0;
      learnerCue(a, true);
    });
    document.querySelectorAll("#airlayer video, #airlayer_cv video").forEach((v) => {
      v.pause();
      v.currentTime = 0;
    });
    triggerAnimationsAtTime(0, { shouldPlayMedia: false, seek: true });
    setCurrentTimeRef.current?.(0, { seek: true });
    tracksAreaRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  // Play reached the end: pause with the playhead parked on the end frame
  // (no rewind, no loop). The next Play press restarts from 0 — see
  // rewindIfEnded.
  const pauseAtEnd = (frame) => {
    stopInterval();
    delete window.__ideaGateBoxId;
    setPausedForInteraction(null);
    setPlaying(false);
    playingRef.current = false;
    currentFrameRef.current = frame;
    setCurrentFrame(frame);
    pauseAnimation?.();
    document.querySelectorAll("#airlayer audio, #airlayer_cv audio").forEach((a) => a.pause());
    document.querySelectorAll("#airlayer video, #airlayer_cv video").forEach((v) => v.pause());
    stopAllYoutubeRef.current?.();
    triggerAnimationsAtTime(frame / FPS, { shouldPlayMedia: false });
    setCurrentTimeRef.current?.(frame / FPS);
    endedAtFrameRef.current = frame;
  };
  pauseAtEndRef.current = pauseAtEnd;

  // Called right before play starts: if the playhead is still parked where
  // play last ended, rewind to frame 0 so Play replays the scene.
  const rewindIfEnded = () => {
    const ended = endedAtFrameRef.current;
    endedAtFrameRef.current = null;
    if (ended == null || currentFrameRef.current !== ended) return;
    currentFrameRef.current = 0;
    setCurrentFrame(0);
    document.querySelectorAll("#airlayer audio, #airlayer_cv audio").forEach((a) => {
      a.currentTime = 0;
    });
    document.querySelectorAll("#airlayer video, #airlayer_cv video").forEach((v) => {
      v.currentTime = 0;
    });
    setCurrentTimeRef.current?.(0);
    tracksAreaRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  };
  rewindIfEndedRef.current = rewindIfEnded;

  // Keep the latest closures in refs so the imperatively-exposed methods never
  // clamp/seek against a stale `duration` captured at mount. The parent calls
  // these via ideaTimelineRef (e.g. after applying an exit animation) and the
  // scene `duration` changes over the component's life — a stale clamp let the
  // playhead overshoot the current scene duration.
  const seekToFrameRef = useRef(seekToFrame);
  seekToFrameRef.current = seekToFrame;
  useEffect(() => {
    const seek = (frame) => seekToFrameRef.current?.(frame);
    registerPlayheadSeeker(seek);
    return () => unregisterPlayheadSeeker(seek);
  }, []);
  handleStopRef.current = handleStop;
  useImperativeHandle(
    ref,
    () => ({
      seekToFrame: (frame) => seekToFrameRef.current?.(frame),
      handleStop: () => handleStopRef.current?.(),
    }),
    []
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Stop on outside click while playing ─────────────────
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!playingRef.current) return;
      // When the interactive gate is active the user needs to click the canvas
      // to complete the interaction — don't stop the timeline.
      if (pausedForInteractionRef.current) return;
      if (rootRef.current && rootRef.current.contains(e.target)) return;
      handleStop();
    };
    document.addEventListener("mousedown", onMouseDown, true);
    return () => document.removeEventListener("mousedown", onMouseDown, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Seek while playing — pause audio, seek, resume after settle ──
  const seekResumeTimerRef = useRef(null);
  const pauseForSeek = () => {
    if (!playingRef.current) return false;
    stopInterval();
    // Now, not on React's update: the seek in the same event must not restart media.
    playingRef.current = false;
    setPlaying(false);
    pauseAnimation?.();
    document.querySelectorAll("#airlayer audio, #airlayer_cv audio").forEach((a) => a.pause());
    return true;
  };
  const resumeAfterSeek = (wasPlaying) => {
    if (!wasPlaying) return;
    clearTimeout(seekResumeTimerRef.current);
    seekResumeTimerRef.current = setTimeout(() => {
      setPlaying(true);
      playAnimation?.();
    }, 150);
  };

  // ── Ruler pointer down (seek) ────────────────────────────
  const handleRulerPointerDown = (e) => {
    const wasPlaying = pauseForSeek();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    // Rect is read live inside seek: edge auto-scroll shifts the ruler under
    // the pointer, so a rect captured at pointerdown goes stale mid-drag.
    let lastClientX = e.clientX;
    const seek = () => {
      const rect = el.getBoundingClientRect();
      seekToFrame(xToFrame(lastClientX - rect.left));
    };
    const scroller = createEdgeAutoScroll(el, seek);
    seek();
    const onMove = (me) => {
      lastClientX = me.clientX;
      scroller.update(me.clientX);
      seek();
    };
    const onUp = () => {
      scroller.stop();
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      resumeAfterSeek(wasPlaying);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  // ── Playhead drag ────────────────────────────────────────
  const handlePlayheadDrag = (e) => {
    e.stopPropagation();
    const wasPlaying = pauseForSeek();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startFrame = currentFrameRef.current;
    const ppf = pps / FPS;
    // Same edge auto-scroll as clip drags: the scroll the container does
    // under a stationary pointer is still playhead movement.
    let lastClientX = startX;
    const moveCore = () =>
      seekToFrame(startFrame + (lastClientX - startX + scroller.scrollDelta()) / ppf);
    const scroller = createEdgeAutoScroll(el, moveCore);
    const onMove = (me) => {
      lastClientX = me.clientX;
      scroller.update(me.clientX);
      moveCore();
    };
    const onUp = () => {
      scroller.stop();
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      resumeAfterSeek(wasPlaying);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  // ── Duration marker drag ─────────────────────────────────
  const handleDurationMarkerDrag = (e) => {
    if (durationLocked) return;
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startDuration = durationRef.current;
    const ppf = pps / FPS;
    const MIN_DURATION = FPS * 5; // minimum 5s

    // Same snap-to-edge feel as clips (TrackClip) — the scene-length bar
    // catches on element start/end frames and the playhead, so it's easy to
    // line the scene end up exactly with the last element instead of eyeballing it.
    const snapThresholdFrames = computeSnapThresholdFrames(pps, FPS);
    const snapTargets = [0, currentFrameRef.current];
    layersRef.current.forEach((l) => {
      snapTargets.push(l.startFrame || 0, l.endFrame || 0);
    });
    const stickySnap = { current: null };

    let latestDuration = startDuration;
    // Same edge auto-scroll as clip/playhead drags — dragging the scene-end
    // marker toward the edge keeps scrolling so it can travel beyond the
    // visible span; the scroll under a stationary pointer still moves it.
    let lastClientX = startX;
    const moveCore = () => {
      const delta = lastClientX - startX + scroller.scrollDelta();
      let next = Math.max(MIN_DURATION, Math.round(startDuration + delta / ppf));
      const snapped = resolveStickySnap(next, snapTargets, snapThresholdFrames, stickySnap);
      if (snapped != null) next = Math.max(MIN_DURATION, snapped);
      setSnapGuideFrame(snapped);
      latestDuration = next;
      userSetDurationRef.current = true;
      setDuration(latestDuration);
    };
    const scroller = createEdgeAutoScroll(el, moveCore);
    const onMove = (me) => {
      lastClientX = me.clientX;
      scroller.update(me.clientX);
      moveCore();
    };
    const onUp = () => {
      scroller.stop();
      setSnapGuideFrame(null);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      onDurationChange?.(latestDuration);
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  // ── Ruler ticks ──────────────────────────────────────────
  const rulerTicks = useMemo(() => {
    const totalSec = timelineSpan / FPS;
    const majorEvery =
      zoom >= 3
        ? 1
        : zoom >= 1.5
          ? 2
          : zoom >= 0.8
            ? 5
            : zoom >= 0.25
              ? 10
              : zoom >= 0.12
                ? 30
                : 60;
    const minorEvery = majorEvery / 5;
    const ticks = [];
    for (let s = 0; s <= totalSec + minorEvery; s = Math.round((s + minorEvery) * 1000) / 1000) {
      const x = s * pps;
      const isMajor =
        Math.abs(s % majorEvery) < 0.001 || Math.abs((s % majorEvery) - majorEvery) < 0.001;
      ticks.push({ x, isMajor, label: isMajor ? formatTime(s * FPS) : null });
    }
    return ticks;
  }, [timelineSpan, zoom, pps]);

  // ── Layer helpers ────────────────────────────────────────
  const toggleVisibility = (id) => {
    setLayers((prev) => {
      const layer = prev.find((l) => l.id === id);
      const newVisible = layer ? layer.visible === false : true; // false→true, true→false
      onLayerVisibilityToggle?.(id, newVisible, layer);
      return prev.map((l) => (l.id === id ? { ...l, visible: newVisible } : l));
    });
  };

  const toggleLock = (id) => {
    setLayers((prev) => {
      const layer = prev.find((l) => l.id === id);
      const newLocked = !layer?.locked;
      onLockToggle?.(id, newLocked, layer);
      return prev.map((l) => (l.id === id ? { ...l, locked: newLocked } : l));
    });
  };

  // Scene-audio only — "repeat for entire scene" toggle from the clip's
  // context menu.
  const toggleLoop = (id) => {
    setLayers((prev) => {
      const layer = prev.find((l) => l.id === id);
      if (!layer || layer.type !== "scene-audio") return prev;
      const newLoop = !layer.audioData?.loopScene;
      onToggleLoop?.(id, layer);
      return prev.map((l) =>
        l.id === id ? { ...l, audioData: { ...l.audioData, loopScene: newLoop } } : l
      );
    });
  };

  // Group-row variants: the label's eye/lock act on every member at once.
  const toggleRowVisibility = (row) => {
    if (!row.isGroup) return toggleVisibility(row.layers[0].id);
    const nextVisible = !row.allVisible;
    const memberIds = new Set(row.layers.map((l) => l.id));
    setLayers((prev) => {
      prev.forEach((l) => {
        if (memberIds.has(l.id)) onLayerVisibilityToggle?.(l.id, nextVisible, l);
      });
      return prev.map((l) => (memberIds.has(l.id) ? { ...l, visible: nextVisible } : l));
    });
  };

  const toggleRowLock = (row) => {
    if (!row.isGroup) return toggleLock(row.layers[0].id);
    const nextLocked = !row.allLocked;
    const memberIds = new Set(row.layers.map((l) => l.id));
    setLayers((prev) => {
      prev.forEach((l) => {
        if (memberIds.has(l.id)) onLockToggle?.(l.id, nextLocked, l);
      });
      return prev.map((l) => (memberIds.has(l.id) ? { ...l, locked: nextLocked } : l));
    });
  };

  const toggleAllVisibility = () => {
    setLayers((prev) => {
      const allVisible = prev.every((l) => l.visible !== false);
      const nextVisible = !allVisible;
      prev.forEach((l) => onLayerVisibilityToggle?.(l.id, nextVisible, l));
      return prev.map((l) => ({ ...l, visible: nextVisible }));
    });
  };

  const toggleAllLock = () => {
    setLayers((prev) => {
      const allLocked = prev.every((l) => l.locked);
      const nextLocked = !allLocked;
      prev.forEach((l) => onLockToggle?.(l.id, nextLocked, l));
      return prev.map((l) => ({ ...l, locked: nextLocked }));
    });
  };

  const handleSelect = (id) => {
    // Re-clicking the selected row means "take me to it", not deselect.
    setSelectedId(id);
    if (id && onSelectLayer) onSelectLayer(id);
    // Direct call — the selectedBoxId prop doesn't change on a re-click.
    ensureSelectedLayerVisible(id);
    // Switch AnimationPanel back to box-mode whenever the parent track is selected.
    setSelectedSubField(null);
  };

  // ── Row drag-reorder (changes stacking order / z-order) ──
  // Dragging a row's grip handle up/down reorders the scene's box array —
  // the SAME array the canvas paints from (later id = on top) and the same
  // one the canvas "bring to front/back" buttons mutate via changeBoxLayer.
  const [rowDropIndicator, setRowDropIndicator] = useState(null); // { top } | null

  // ── Clip drag/resize snap guide (horizontal) ──────────────
  const [snapGuideFrame, setSnapGuideFrame] = useState(null);

  // Box rows persist z-order; narration rows persist display-only rowOrder.
  // Persist box z-order and narration-only display order from one row sequence.
  const persistRowSequence = (frontFirstIds, draggedId) => {
    const findLayer = (id) => layersRef.current.find((l) => l.id === id);

    const sequence = [...frontFirstIds].reverse();
    const oldSequence = layersRef.current
      .map((l) => l.id)
      .slice()
      .reverse();

    const oldBoxOrder = oldSequence.filter((id) => findLayer(id)?.type !== "scene-audio");
    const newBoxOrder = sequence.filter((id) => findLayer(id)?.type !== "scene-audio");
    const boxOrderChanged =
      newBoxOrder.length !== oldBoxOrder.length ||
      newBoxOrder.some((id, i) => id !== oldBoxOrder[i]);
    if (boxOrderChanged) {
      const scenePage =
        (containedViewSelected && containedViewSelected !== 0
          ? containedViewsById[containedViewSelected]
          : navItemsById[navItemSelected]) || {};
      const rawBoxOrder = Array.isArray(scenePage.boxes) ? scenePage.boxes : [];
      // Preserve boxes excluded from the visible timeline.
      const extra = rawBoxOrder.filter((id) => !newBoxOrder.includes(id));
      onLayerReorder?.(currentScene, [...newBoxOrder, ...extra], draggedId);
    }

    // Recompute narration positions on the same numeric axis as box z-index.
    const minBoxZIndex = layersRef.current.reduce(
      (m, l) =>
        l.type !== "scene-audio" && typeof l.zIndex === "number" ? Math.min(m, l.zIndex) : m,
      Infinity
    );
    const sceneAudioBase = Number.isFinite(minBoxZIndex) ? minBoxZIndex : 0;
    const defaultPos = (l) => sceneAudioBase - 1 - (l.soundIndex ?? 0) / 1e6;
    const computed = new Map(); // id -> freshly recomputed position, filled left-to-right
    const effectivePos = (id) => {
      if (computed.has(id)) return computed.get(id);
      const l = findLayer(id);
      if (!l) return null;
      return l.type === "scene-audio"
        ? typeof l.rowOrder === "number"
          ? l.rowOrder
          : defaultPos(l)
        : l.zIndex;
    };
    const updates = [];
    sequence.forEach((id, i) => {
      const l = findLayer(id);
      if (!l || l.type !== "scene-audio") return;
      const prevPos = i > 0 ? effectivePos(sequence[i - 1]) : null;
      const nextPos = i < sequence.length - 1 ? effectivePos(sequence[i + 1]) : null;
      const newPos =
        prevPos != null && nextPos != null
          ? (prevPos + nextPos) / 2
          : prevPos != null
            ? prevPos + 1
            : nextPos != null
              ? nextPos - 1
              : 0;
      computed.set(id, newPos);
      const currentPos = typeof l.rowOrder === "number" ? l.rowOrder : defaultPos(l);
      if (Math.abs(newPos - currentPos) > 1e-9) {
        updates.push({ soundId: id, rowOrder: newPos });
      }
    });
    if (updates.length > 0) {
      onSceneAudioReorder?.(currentScene, updates);
    }
  };

  // Member ids of every rendered row in display (front-first) order — the
  // relative z-order INSIDE a group is preserved (row.layers is sorted by
  // startFrame, which is a different axis).
  const buildIdsByRowKey = () => {
    const map = new Map();
    layersRef.current.forEach((l) => {
      const key = rowKeyForLayer(l);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l.id);
    });
    return map;
  };

  // Cached rects of the rendered rows for one drag gesture.
  const captureRowRects = () => {
    const tracksEl = tracksElRef.current;
    if (!tracksEl) return null;
    const rowsByKey = new Map(rowsRef.current.map((r) => [r.key, r]));
    const eligible = Array.from(tracksEl.querySelectorAll(".it-track-row[data-row-key]"))
      .map((el) => ({ key: el.dataset.rowKey, rect: el.getBoundingClientRect() }))
      .filter(({ key }) => rowsByKey.has(key));
    return { tracksEl, rowsByKey, eligible, tracksRect: tracksEl.getBoundingClientRect() };
  };

  const rowDropIndicatorTop = (eligible, insertIndex, tracksRect) => {
    const last = eligible[eligible.length - 1];
    return insertIndex < eligible.length
      ? eligible[insertIndex].rect.top - tracksRect.top
      : last.rect.top + last.rect.height - tracksRect.top;
  };

  // Narrations (scene-audio) live in a pinned bottom zone — they have no
  // canvas presence, so they can never sit above a visible element's row, and
  // no box row can be dropped below them. Clickable sound BOXES are ordinary
  // canvas elements and move freely.
  const clampInsertForType = (insertIndex, isSceneAudio, eligible, rowsByKey) => {
    let boundary = eligible.findIndex((e) => rowsByKey.get(e.key)?.type === "scene-audio");
    if (boundary === -1) boundary = eligible.length;
    return isSceneAudio ? Math.max(insertIndex, boundary) : Math.min(insertIndex, boundary);
  };

  const createRowReorderSession = (row) => {
    if (row.allLocked) return null;

    const captured = captureRowRects();
    if (!captured) return null;
    const { eligible, tracksRect, rowsByKey } = captured;
    if (eligible.length < 2) return null;

    const startKeys = eligible.map((e) => e.key);
    if (!startKeys.includes(row.key)) return null;
    let lastInsertIndex = null;

    const computeInsertIndex = (clientY) => {
      for (let i = 0; i < eligible.length; i++) {
        if (clientY < eligible[i].rect.top + eligible[i].rect.height / 2) {
          return clampInsertForType(i, row.type === "scene-audio", eligible, rowsByKey);
        }
      }
      return clampInsertForType(eligible.length, row.type === "scene-audio", eligible, rowsByKey);
    };

    return {
      onMove(clientY) {
        const insertIndex = computeInsertIndex(clientY);
        lastInsertIndex = insertIndex;
        setRowDropIndicator({ top: rowDropIndicatorTop(eligible, insertIndex, tracksRect) });
      },
      cancel() {
        setRowDropIndicator(null);
      },
      commit() {
        setRowDropIndicator(null);
        if (lastInsertIndex === null) return;

        const draggedIndex = startKeys.indexOf(row.key);
        if (lastInsertIndex === draggedIndex || lastInsertIndex === draggedIndex + 1) return; // dropped back in place

        const without = startKeys.filter((k) => k !== row.key);
        let insertAt = lastInsertIndex;
        if (draggedIndex < lastInsertIndex) insertAt -= 1;
        const reorderedKeys = [...without];
        reorderedKeys.splice(insertAt, 0, row.key);

        const idsByRowKey = buildIdsByRowKey();
        const frontFirstIds = reorderedKeys.flatMap((k) => idsByRowKey.get(k) || []);
        persistRowSequence(frontFirstIds, row.layers[0].id);
      },
    };
  };

  // ── Clip drag session ────────────────────────────────────
  const [dragPreview, setDragPreview] = useState(null); // { targetRowKey, startFrame, endFrame } | null
  const [dragSourceClipId, setDragSourceClipId] = useState(null);

  const createClipDragSession = (layer) => {
    if (layer.locked) return null;
    const captured = captureRowRects();
    if (!captured) return null;
    const { rowsByKey, eligible, tracksRect } = captured;

    const sourceRow = rowsRef.current.find((r) => r.layers.some((l) => l.id === layer.id));
    if (!sourceRow) return null;
    if (eligible.length < 2 && !sourceRow.isGroup) return null;

    const clipLen = Math.max(1, layer.endFrame - layer.startFrame);
    const joinable = GROUPABLE_TYPES.has(layer.type);
    const snapThreshold = computeSnapThresholdFrames(pps, FPS);
    const stickyStart = { current: null };
    const stickyEnd = { current: null };

    let target = null; // { kind:"between", insertIndex } | { kind:"row", rowKey, startFrame, endFrame, shifts, snappedFrame }
    let lastPt = null;

    isDraggingRef.current = true;
    setDragSourceClipId(layer.id);

    const snapDesired = (desired, candidateLayers) => {
      const targets = [0, currentFrameRef.current, durationRef.current];
      candidateLayers.forEach((l) => {
        if (l.id !== layer.id) targets.push(l.startFrame, l.endFrame);
      });
      const s = resolveStickySnap(desired, targets, snapThreshold, stickyStart);
      const e = resolveStickySnap(desired + clipLen, targets, snapThreshold, stickyEnd);
      let out = desired;
      let snappedFrame = null;
      if (s != null || e != null) {
        const sd = s != null ? Math.abs(desired - s) : Infinity;
        const ed = e != null ? Math.abs(desired + clipLen - e) : Infinity;
        if (sd <= ed) {
          out = Math.max(0, s);
          snappedFrame = s;
        } else {
          out = Math.max(0, e - clipLen);
          snappedFrame = e;
        }
      }
      return { out, snappedFrame };
    };

    const betweenTarget = (insertIndex, desiredStartFrame) => {
      const { out, snappedFrame } = snapDesired(desiredStartFrame, layersRef.current);
      return {
        kind: "between",
        insertIndex: clampInsertForType(
          insertIndex,
          layer.type === "scene-audio",
          eligible,
          rowsByKey
        ),
        startFrame: out,
        endFrame: out + clipLen,
        snappedFrame,
      };
    };

    const resolveTarget = (clientX, clientY, desiredStartFrame) => {
      for (let i = 0; i < eligible.length; i++) {
        const { key, rect } = eligible[i];
        if (clientY < rect.top + BOUNDARY_PX) return betweenTarget(i, desiredStartFrame);
        if (clientY <= rect.bottom - BOUNDARY_PX) {
          const row = rowsByKey.get(key);
          const compatible = key === sourceRow.key || (joinable && row && row.type === layer.type);
          if (compatible) {
            const { out, snappedFrame } = snapDesired(desiredStartFrame, row.layers);
            const members = row.layers
              .filter((l) => l.id !== layer.id)
              .map(({ id, startFrame, endFrame }) => ({ id, startFrame, endFrame }));
            const placement = resolveRowInsertion({ desiredStart: out, clipLen, members });
            return { kind: "row", rowKey: key, ...placement, snappedFrame };
          }
          // Incompatible row: fall back to the nearest between-slot.
          const mid = rect.top + rect.height / 2;
          return betweenTarget(clientY < mid ? i : i + 1, desiredStartFrame);
        }
        // bottom boundary band → next iteration's top band (or past the end)
      }
      return betweenTarget(eligible.length, desiredStartFrame);
    };

    const render = () => {
      if (!target) return;
      if (target.kind === "between") {
        setDragPreview(null);
        setSnapGuideFrame(target.snappedFrame ?? null);
        // startFrame/endFrame drive the slim ghost bar on the line — the X
        // where the new standalone row's clip will land.
        setRowDropIndicator({
          top: rowDropIndicatorTop(eligible, target.insertIndex, tracksRect),
          startFrame: target.startFrame,
          endFrame: target.endFrame,
        });
      } else {
        setRowDropIndicator(null);
        setDragPreview({
          targetRowKey: target.rowKey,
          startFrame: target.startFrame,
          endFrame: target.endFrame,
        });
        setSnapGuideFrame(target.snappedFrame ?? null);
      }
    };

    const clearOverlays = () => {
      setRowDropIndicator(null);
      setDragPreview(null);
      setSnapGuideFrame(null);
      setDragSourceClipId(null);
    };

    const applyRowDrop = (t, trackId) => {
      const srcLayer = layersRef.current.find((l) => l.id === layer.id) || layer;
      onLayerUpdate?.(layer.id, t.startFrame, t.endFrame, trackId ? { trackId } : {}, srcLayer);
      if (t.shifts.length > 0) {
        const boxUpdates = [];
        t.shifts.forEach(({ id, delta }) => {
          const m = layersRef.current.find((l) => l.id === id);
          if (!m) return;
          // Narration members persist through editSound (seconds), not
          // box.timeline — route them through the type-aware handler.
          if (m.type === "scene-audio") {
            onLayerUpdate?.(m.id, m.startFrame + delta, m.endFrame + delta, {}, m);
            return;
          }
          const timeline = {
            startFrame: m.startFrame + delta,
            endFrame: m.endFrame + delta,
          };
          // Keep an autoplay video's play window anchored to its clip.
          if (typeof m.box?.timeline?.playStartFrame === "number") {
            timeline.playStartFrame = m.box.timeline.playStartFrame + delta;
          }
          boxUpdates.push({ id, timeline });
        });
        if (boxUpdates.length > 0) onBatchTimelineUpdate?.(boxUpdates);
      }
      // Optimistic local mirror so the drop lands without waiting for redux.
      setLayers((prev) =>
        prev.map((l) => {
          if (l.id === layer.id) {
            return {
              ...l,
              startFrame: t.startFrame,
              endFrame: t.endFrame,
              ...(trackId ? { trackId } : {}),
            };
          }
          const sh = t.shifts.find((s) => s.id === l.id);
          if (sh)
            return { ...l, startFrame: l.startFrame + sh.delta, endFrame: l.endFrame + sh.delta };
          return l;
        })
      );
    };

    return {
      onMove(clientX, clientY, desiredStartFrame) {
        const t = resolveTarget(clientX, clientY, desiredStartFrame);
        // Tiny pointer jitter must not flip the target kind at a row boundary.
        if (
          target &&
          lastPt &&
          t.kind !== target.kind &&
          Math.hypot(clientX - lastPt.x, clientY - lastPt.y) < 3
        ) {
          return;
        }
        lastPt = { x: clientX, y: clientY };
        target = t;
        render();
      },
      cancel() {
        clearOverlays();
        releaseDragGate();
      },
      commit() {
        clearOverlays();
        if (!target) {
          releaseDragGate();
          return;
        }
        window.cropUndoGroup = "clipdrop_" + Date.now();
        try {
          if (target.kind === "between") {
            const idsByRowKey = buildIdsByRowKey();
            const units = eligible.map(({ key }) =>
              (idsByRowKey.get(key) || []).filter((id) => id !== layer.id)
            );
            const frontFirstIds = [];
            units.forEach((u, i) => {
              if (i === target.insertIndex) frontFirstIds.push(layer.id);
              frontFirstIds.push(...u);
            });
            if (target.insertIndex >= units.length) frontFirstIds.push(layer.id);
            if (layer.trackId) {
              onLayerTrackChange?.(layer.id, null, layer);
              setLayers((prev) =>
                prev.map((l) => (l.id === layer.id ? { ...l, trackId: null } : l))
              );
            }
            if (target.startFrame !== layer.startFrame || target.endFrame !== layer.endFrame) {
              const srcLayer = layersRef.current.find((l) => l.id === layer.id) || layer;
              // Prevent stale audioData from restoring the old trackId.
              onLayerUpdate?.(
                layer.id,
                target.startFrame,
                target.endFrame,
                layer.trackId ? { trackId: null } : {},
                srcLayer
              );
              setLayers((prev) =>
                prev.map((l) =>
                  l.id === layer.id
                    ? { ...l, startFrame: target.startFrame, endFrame: target.endFrame }
                    : l
                )
              );
            }
            persistRowSequence(frontFirstIds, layer.id);
          } else if (target.rowKey === sourceRow.key) {
            applyRowDrop(target, undefined);
          } else {
            const targetRow = rowsByKey.get(target.rowKey);
            let trackId = targetRow.trackId;
            if (!trackId) {
              trackId = "tr-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
              onLayerTrackChange?.(targetRow.layers[0].id, trackId, targetRow.layers[0]);
              setLayers((prev) =>
                prev.map((l) => (l.id === targetRow.layers[0].id ? { ...l, trackId } : l))
              );
            }
            applyRowDrop(target, trackId);
            const idsByRowKey = buildIdsByRowKey();
            const frontFirstIds = [];
            eligible.forEach(({ key }) => {
              const u = (idsByRowKey.get(key) || []).filter((id) => id !== layer.id);
              if (key === target.rowKey) frontFirstIds.push(layer.id, ...u);
              else frontFirstIds.push(...u);
            });
            persistRowSequence(frontFirstIds, layer.id);
          }
        } finally {
          window.cropUndoGroup = null;
        }
        setTimeout(() => releaseDragGate(), 100);
      },
    };
  };

  // Grip handle in the row label — reordering is the ONLY thing this
  // gesture can do, so it starts a session immediately on pointerdown.
  const handleRowDragStart = (e, row) => {
    e.stopPropagation();
    e.preventDefault();
    const session = createRowReorderSession(row);
    if (!session) return;

    const startY = e.clientY;
    let didMove = false;
    let lastY = startY;

    // Vertical edge auto-scroll: a long timeline can hold the drop target
    // off-screen. The session's rects were cached at gesture start, so the
    // pointer is mapped by the scroll delta into that coordinate space.
    const scroller = createEdgeAutoScroll(e.currentTarget, () => step());
    const step = () => {
      if (!didMove && Math.abs(lastY - startY) < 3) return;
      didMove = true;
      session.onMove(lastY + scroller.scrollDeltaY());
    };

    const onMove = (me) => {
      lastY = me.clientY;
      scroller.update(undefined, me.clientY);
      step();
    };

    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      scroller.stop();
      if (didMove) session.commit();
      else session.cancel();
    };

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const handleLayerUpdate = (id, newStart, newEnd, extra = {}) => {
    isDraggingRef.current = true;
    const { resizeEdge, ...layerExtra } = extra;
    if (resizeEdge === "right" && !resizeRippleRef.current) {
      const ownRow = rowsRef.current.find((r) => r.layers.some((l) => l.id === id));
      const sourceIndex = ownRow?.layers.findIndex((l) => l.id === id) ?? -1;
      resizeRippleRef.current = {
        id,
        followers:
          sourceIndex >= 0
            ? ownRow.layers.slice(sourceIndex + 1).map((l) => ({
                id: l.id,
                startFrame: l.startFrame,
                endFrame: l.endFrame,
                layer: l,
              }))
            : [],
      };
    }
    const ripple =
      resizeEdge === "right" && resizeRippleRef.current?.id === id
        ? resolveRightResizeRipple({
            endFrame: newEnd,
            followers: resizeRippleRef.current.followers,
          })
        : [];
    const shiftsById = new Map(ripple.map((shift) => [shift.id, shift.delta]));
    setLayers((prev) =>
      prev.map((l) => {
        if (l.id !== id) {
          const delta = shiftsById.get(l.id);
          if (delta == null) {
            // A previous pointer move may have pushed this follower. Always
            // restore from the gesture baseline when a gap absorbs the ripple.
            const baseline = resizeRippleRef.current?.followers.find((f) => f.id === l.id);
            return baseline
              ? { ...l, startFrame: baseline.startFrame, endFrame: baseline.endFrame }
              : l;
          }
          const baseline = resizeRippleRef.current.followers.find((f) => f.id === l.id);
          return {
            ...l,
            startFrame: baseline.startFrame + delta,
            endFrame: baseline.endFrame + delta,
          };
        }
        // Atomic sub-fields are positioned relative to the parent (see
        // buildAtomicChildLayers): start follows the parent start; end follows
        // the parent end unless the field has its own exit animation. They are
        // then clamped inside the parent bounds — so a field WITH an exit
        // shrinks with the parent too instead of poking out past its end.
        // Recompute live on every parent edit so they don't stay stale until an
        // unrelated rebuild (the drag gate blocks the one that would fix them).
        const deltaStart = newStart - (l.startFrame || 0);
        const childLayers = Array.isArray(l.childLayers)
          ? l.childLayers.map((c) => {
              let cs = c.startFrame + deltaStart;
              let ce = c.exitName != null ? c.endFrame + deltaStart : newEnd;
              cs = Math.max(newStart, Math.min(cs, newEnd));
              ce = Math.max(cs, Math.min(ce, newEnd));
              return { ...c, startFrame: cs, endFrame: ce };
            })
          : l.childLayers;
        return { ...l, startFrame: newStart, endFrame: newEnd, ...layerExtra, childLayers };
      })
    );
    clearTimeout(handleLayerUpdate._timer);
    handleLayerUpdate._timer = setTimeout(() => {
      releaseDragGate();
    }, 500);
  };

  const handleLayerUpdateEnd = (id, newStart, newEnd, extra = {}) => {
    clearTimeout(handleLayerUpdate._timer);
    const layer = layersRef.current.find((l) => l.id === id);
    const { resizeEdge, ...layerExtra } = extra;
    onLayerUpdate?.(id, newStart, newEnd, layerExtra, layer);
    const session =
      resizeEdge === "right" && resizeRippleRef.current?.id === id ? resizeRippleRef.current : null;
    if (session) {
      const shifts = resolveRightResizeRipple({ endFrame: newEnd, followers: session.followers });
      const boxUpdates = [];
      shifts.forEach(({ id: followerId, delta }) => {
        const follower = session.followers.find((f) => f.id === followerId)?.layer;
        if (!follower) return;
        const shiftedStart = follower.startFrame + delta;
        const shiftedEnd = follower.endFrame + delta;
        if (follower.type === "scene-audio") {
          onLayerUpdate?.(followerId, shiftedStart, shiftedEnd, {}, follower);
        } else {
          const timeline = { startFrame: shiftedStart, endFrame: shiftedEnd };
          if (typeof follower.box?.timeline?.playStartFrame === "number") {
            timeline.playStartFrame = follower.box.timeline.playStartFrame + delta;
          }
          boxUpdates.push({ id: followerId, timeline });
        }
      });
      if (boxUpdates.length > 0) onBatchTimelineUpdate?.(boxUpdates);
    }
    resizeRippleRef.current = null;
    // Dragging a clip past the marker no longer moves the marker — the
    // timeline span (ruler/playback) covers content beyond it on its own.
    setTimeout(() => {
      releaseDragGate();
    }, 100);
  };

  // ── Align to neighbor (deterministic, no freehand precision needed) ──
  // "previous": snap this clip's start to the nearest other clip's end at
  // or before it, shifting the whole clip (length fixed). "next": snap this
  // clip's end to the nearest other clip's start at or after it.
  const handleAlignToNeighbor = (layerId, direction) => {
    const layer = layersRef.current.find((l) => l.id === layerId);
    if (!layer) return;
    const clipLen = layer.endFrame - layer.startFrame;
    let newStart = layer.startFrame;
    let newEnd = layer.endFrame;

    // Inside a shared row, "neighbor" means a sibling of the same row —
    // aligning against a clip from another row would create overlap here.
    const ownRow = rowsRef.current.find((r) => r.layers.some((l) => l.id === layerId));
    const candidates = ownRow?.isGroup ? ownRow.layers : layersRef.current;

    if (direction === "previous") {
      let prevEnd = null;
      candidates.forEach((l) => {
        if (l.id === layerId) return;
        const lEnd = l.endFrame || 0;
        if (lEnd <= layer.startFrame && (prevEnd === null || lEnd > prevEnd)) prevEnd = lEnd;
      });
      if (prevEnd === null) return;
      newStart = prevEnd;
      newEnd = newStart + clipLen;
    } else {
      let nextStart = null;
      candidates.forEach((l) => {
        if (l.id === layerId) return;
        const lStart = l.startFrame || 0;
        if (lStart >= layer.endFrame && (nextStart === null || lStart < nextStart))
          nextStart = lStart;
      });
      if (nextStart === null) return;
      newEnd = nextStart;
      newStart = newEnd - clipLen;
    }

    setLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, startFrame: newStart, endFrame: newEnd } : l))
    );
    onLayerUpdate?.(layerId, newStart, newEnd, {}, layer);
  };

  // ── Delete layer ─────────────────────────────────────────
  const handleDelete = (layerId) => {
    const layer = layersRef.current.find((l) => l.id === layerId);
    if (!layer) return;
    // Optimistik güncelleme: Redux yanıtı beklemeden anında kaldır
    setLayers((prev) => prev.filter((l) => l.id !== layerId));
    onDeleteLayer?.(layer);
  };

  // ── Keyboard Delete ───────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      // Input/textarea odakta ise timeline'ı ilgilendirmiyor
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable)
        return;
      // When an InteractivePlugin sub-item is selected via the canvas-workflow
      // slice, EditorApp.keyListener owns the keypress and dispatches
      // REMOVE_INTERACTIVE_ITEM. Without this guard, this listener ALSO fires
      // and deletes the parent timeline layer (the InteractivePlugin box) on
      // the same keypress — wiping the box and its remaining sub-items.
      const istudio =
        store && typeof store.getState === "function" ? store.getState().interactivityStudio : null;
      if (istudio && istudio.selectedItemId) return;
      const id = selectedId;
      if (!id) return;
      const layer = layersRef.current.find((l) => l.id === id);
      if (!layer) return;
      e.preventDefault();
      handleDelete(id);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedId, store]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Split layer at current playhead ──────────────────────
  const handleSplit = (layerId) => {
    const layer = layersRef.current.find((l) => l.id === layerId);
    if (!layer) return;

    const splitFrame = currentFrameRef.current;
    // Split sadece clip içindeyse geçerli (en az 1 frame boşluk)
    if (splitFrame <= layer.startFrame + 1 || splitFrame >= layer.endFrame - 1) return;

    const trimStart = layer.trimStart ?? 0;
    // Sol parça: startFrame → splitFrame, trimEnd = trimStart + sol parça süresi
    const leftTrimEnd = trimStart + (splitFrame - layer.startFrame);
    // Sağ parça: splitFrame → endFrame, trimStart = leftTrimEnd
    const rightTrimStart = leftTrimEnd;
    const rightTrimEnd = layer.trimEnd ?? null;
    // Both halves share one splitGroup id — the layer's own id the first time
    // it's cut, or its existing group if it's already a split piece being cut
    // again — so a chain of re-splits all stays visually linked.
    const splitGroup = layer.splitGroup || layerId;

    // One Ctrl+Z reverts the whole split (see groupBy in reducers.jsx).
    const previousUndoGroup = window.cropUndoGroup;
    window.cropUndoGroup = `split-${layerId}-${Date.now()}`;
    try {
      // Sol parçayı güncelle
      onLayerUpdate?.(
        layerId,
        layer.startFrame,
        splitFrame,
        { trimEnd: leftTrimEnd, splitGroup },
        layer
      );
      setLayers((prev) =>
        prev.map((l) =>
          l.id === layerId ? { ...l, endFrame: splitFrame, trimEnd: leftTrimEnd, splitGroup } : l
        )
      );

      // Sağ parçayı yeni box / sound olarak oluştur
      if (onSplitLayer) {
        onSplitLayer({
          sourceLayer: layer,
          startFrame: splitFrame,
          endFrame: layer.endFrame,
          trimStart: rightTrimStart,
          trimEnd: rightTrimEnd,
          splitGroup,
        });
      }
    } finally {
      window.cropUndoGroup = previousUndoGroup ?? null;
    }
  };

  // ── Cut & delete the head at the playhead ────────────────
  // "Kes ve sil": split, throw away the piece before the playhead, and slide
  // what is left back to where the clip started so no gap opens in its place.
  // Done as ONE update on the existing clip rather than split + delete: no
  // throwaway box/sound is created, the clip keeps its id (selection, trackId,
  // animations and z-order all survive) and it is a single undo step.
  const handleTrimHeadAtPlayhead = (layerId) => {
    const layer = layersRef.current.find((l) => l.id === layerId);
    if (!layer || layer.locked) return;

    const result = resolveHeadTrim({ layer, cutFrame: currentFrameRef.current, fps: FPS });
    if (!result) return;
    const { startFrame, endFrame, extra, layerPatch } = result;

    setLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, startFrame, endFrame, ...layerPatch } : l))
    );
    onLayerUpdate?.(layerId, startFrame, endFrame, extra, layer);
  };

  const playheadX = frameToX(currentFrame);

  return (
    <div className="it-timeline" ref={rootRef}>
      <Toolbar
        playing={playing}
        currentFrame={currentFrame}
        duration={duration}
        speed={speed}
        onSpeedChange={changeSpeed}
        maxSpeed={maxSpeed}
        speedLimitReason={maxSpeed ? i18n.t("messages.previewSpeed.youtubeLimit") : null}
        onDurationChange={(newDuration) => {
          if (durationLocked) return;
          setDuration(newDuration);
          onDurationChange?.(newDuration);
        }}
        durationLocked={durationLocked}
        onToggleDurationLock={toggleDurationLock}
        collapsed={collapsed}
        knollingViewVisible={knollingViewVisible}
        onToggleCollapse={onToggleCollapse}
        waiting={playing && mediaWaiting}
        onPlay={() => {
          if (!playing) {
            unlockAudioGraph();
            rewindIfEnded();
            onAnimationReset?.();
            // Same-task repaint after the reset — see the event onPlay handler.
            playingRef.current = true;
            triggerAnimationsAtTime(currentFrameRef.current / FPS, { shouldPlayMedia: false });
            setPlaying(true);
            playAnimation?.();
          } else {
            pauseNow();
          }
        }}
        onSeekStart={() => {
          const wasPlaying = pauseForSeek();
          seekToFrame(0);
          resumeAfterSeek(wasPlaying);
          tracksAreaRef.current?.scrollTo({ left: 0, behavior: "smooth" });
        }}
        onSyncScene={onSyncScene}
        onMotionizeScene={onMotionizeScene}
        onToggleKnolling={onToggleKnolling}
        syncingScene={syncingScene}
        syncSceneStatus={syncSceneStatus}
      />

      {/* Single scrollable body — labels + tracks scroll together */}
      <div
        className="it-tracks-area"
        ref={tracksAreaRef}
        onPointerDownCapture={() => {
          autoFitRef.current = false;
        }}
      >
        {/* Ruler row: sticky corner + ruler */}
        <div className="it-ruler-row">
          <div className="it-label-corner">
            <div className="it-zoom-mini" role="group" aria-label="Zoom">
              <button
                className="it-btn--icon it-btn--xs"
                title={i18n.t("ZoomOut") || "Uzaklaştır"}
                disabled={zoom <= MIN_ZOOM}
                onClick={() =>
                  setManualZoom(
                    zoom > MIN_STEP_ZOOM
                      ? Math.max(MIN_STEP_ZOOM, parseFloat((zoom - 0.25).toFixed(2)))
                      : Math.max(MIN_ZOOM, parseFloat((zoom / 1.5).toFixed(3)))
                  )
                }
              >
                <LuMinus size={13} />
              </button>
              <button
                className="it-zoom-mini__value"
                title={i18n.t("ZoomReset") || "Zoom'u sıfırla (%100)"}
                onClick={() => setManualZoom(1)}
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                className="it-btn--icon it-btn--xs"
                title={i18n.t("ZoomIn") || "Yakınlaştır"}
                disabled={zoom >= MAX_ZOOM}
                onClick={() =>
                  setManualZoom(
                    zoom < MIN_STEP_ZOOM
                      ? Math.min(MIN_STEP_ZOOM, parseFloat((zoom * 1.5).toFixed(3)))
                      : Math.min(MAX_ZOOM, parseFloat((zoom + 0.25).toFixed(2)))
                  )
                }
              >
                <LuPlus size={13} />
              </button>
            </div>
            <div className="it-corner-toggles">
              <button
                className={`it-btn--icon it-btn--xs${ghostMode ? " is-on" : ""}`}
                aria-pressed={!!ghostMode}
                title={
                  ghostMode
                    ? "Sadece bu andaki elementleri göster"
                    : "Tüm elementleri göster (zaman dışındakiler soluk)"
                }
                onClick={handleGhostToggle}
              >
                <LuSquareDashed size={14} />
              </button>
              <button
                className="it-btn--icon it-btn--xs"
                title={
                  layers.every((l) => l.visible !== false)
                    ? i18n.t("HideAll") || "Tümünü Gizle"
                    : i18n.t("ShowAll") || "Tümünü Göster"
                }
                onClick={toggleAllVisibility}
              >
                {layers.every((l) => l.visible !== false) ? (
                  <LuEye size={14} />
                ) : (
                  <LuEyeOff size={14} />
                )}
              </button>
              <button
                className="it-btn--icon it-btn--xs"
                title={
                  layers.every((l) => l.locked)
                    ? i18n.t("UnlockAll") || "Tümünü Aç"
                    : i18n.t("LockAll") || "Tümünü Kilitle"
                }
                onClick={toggleAllLock}
              >
                {layers.every((l) => l.locked) ? <LuLock size={14} /> : <LuLockOpen size={14} />}
              </button>
            </div>
          </div>
          <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
            <Ruler
              totalWidth={totalWidth}
              rulerTicks={rulerTicks}
              playheadX={playheadX}
              onPointerDown={handleRulerPointerDown}
              onPlayheadDrag={handlePlayheadDrag}
            />
          </div>
        </div>

        {/* All track rows */}
        <div className="it-tracks" ref={tracksElRef} style={{ minWidth: LABEL_WIDTH + totalWidth }}>
          <PlayheadLine x={playheadX + LABEL_WIDTH} onPointerDown={handlePlayheadDrag} />
          {rowDropIndicator && (
            <>
              <div className="it-row-drop-indicator" style={{ top: rowDropIndicator.top }} />
              {rowDropIndicator.startFrame != null && (
                <div
                  className="it-row-drop-ghost"
                  style={{
                    top: rowDropIndicator.top - 5,
                    left: LABEL_WIDTH + frameToX(rowDropIndicator.startFrame),
                    width: Math.max(
                      16,
                      frameToX(rowDropIndicator.endFrame - rowDropIndicator.startFrame)
                    ),
                  }}
                />
              )}
            </>
          )}
          {snapGuideFrame != null && (
            <div
              className="it-snap-guide-line"
              style={{ left: frameToX(snapGuideFrame) + LABEL_WIDTH }}
            />
          )}
          <div
            className={`it-duration-marker${durationLocked ? " it-duration-marker--locked" : ""}`}
            style={{ left: frameToX(duration) + LABEL_WIDTH }}
            onPointerDown={handleDurationMarkerDrag}
          >
            <button
              className="it-duration-marker__lock"
              title={durationLocked ? "Kilidi aç" : "Süreyi kilitle"}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                toggleDurationLock();
              }}
            >
              {durationLocked ? <LuLock size={10} /> : <LuLockOpen size={10} />}
            </button>
          </div>

          {layers.length === 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "80px",
                color: "#9ca3af",
                fontSize: "13px",
                paddingLeft: LABEL_WIDTH,
              }}
            >
              {i18n.t("NoElementInScene")}
            </div>
          )}

          {/* Interactive gate overlay — renders a vertical purple bar at the
              frozen playhead position while waiting for interaction to complete.
              Shows a tooltip + "Atla" button so authors can skip past the gate
              during preview without completing the full interaction flow. */}
          {pausedForInteraction &&
            (() => {
              const gateX = frameToX(pausedForInteraction.startFrame) + LABEL_WIDTH;
              const handleSkip = () => {
                try {
                  document.dispatchEvent(
                    new CustomEvent("aidea-interactive-complete", {
                      detail: {
                        reason: "skip",
                        boxId: pausedForInteraction.boxId,
                        sceneId: null,
                      },
                      bubbles: true,
                    })
                  );
                } catch (_) {}
              };
              return (
                <div
                  className="it-interactive-gate"
                  style={{ left: gateX }}
                  title={
                    i18n.t("Timeline.WaitingForInteractive") ||
                    "Timeline interaktif element bekliyor"
                  }
                >
                  <div className="it-interactive-gate__line" />
                  <div className="it-interactive-gate__badge">
                    <span className="it-interactive-gate__badge-icon">⚡</span>
                    <span className="it-interactive-gate__badge-text">
                      {i18n.t("Interactive") || "İnteraktif"}
                    </span>
                    <button
                      type="button"
                      className="it-interactive-gate__skip"
                      onClick={handleSkip}
                      title={
                        i18n.t("Timeline.SkipInteractive") ||
                        "Interaktifi atla ve timeline'ı devam ettir"
                      }
                    >
                      {i18n.t("Skip") || "Atla"} →
                    </button>
                  </div>
                </div>
              );
            })()}

          {rows.map((row) => {
            const isMain = row.type === "video" || row.type === "scene-audio";
            const rowColor = TRACK_COLORS[row.type] || "#6b7280";
            // Single-member rows keep the classic per-element label; only true
            // groups switch to the type-icon + member-count label.
            const soloLayer = row.isGroup ? null : row.layers[0];
            const rowSelected = !!soloLayer && selectedId === soloLayer.id;
            const isDropTarget = dragPreview?.targetRowKey === row.key;
            const groupLabel =
              row.type === "image"
                ? i18n.t("TimelineRowImages") || "Görseller"
                : row.type === "video"
                  ? i18n.t("TimelineRowVideos") || "Videolar"
                  : row.type === "text"
                    ? i18n.t("TimelineRowTexts") || "Metinler"
                    : i18n.t("TimelineRowAudios") || "Sesler";
            const GroupIcon = TRACK_ICONS[row.type] || LuLayoutGrid;

            // Solo-label visuals (thumb / plugin icon) — group rows don't use them.
            const soloPluginKey = soloLayer
              ? (
                  pluginToolbars[soloLayer.id]?.pluginId ||
                  soloLayer.box?.pluginId ||
                  ""
                ).toLowerCase()
              : "";
            // Audio rows always use their TRACK_ICONS entry (megaphone for
            // click/cue sound boxes, vocal mic for scene narrations) so the
            // two audio row kinds are tellable apart at a glance.
            const isAudioType = row.type === "audio" || row.type === "scene-audio";
            const SoloIcon = soloLayer
              ? isAudioType
                ? TRACK_ICONS[row.type]
                : PLUGIN_ICONS[soloPluginKey] || TRACK_ICONS[row.type] || TRACK_ICONS.element
              : null;
            // Audio has no visual frame — a thumbUrl here is the audio file URL,
            // which renders as a broken <img> placeholder. Force the type icon.
            const showThumb =
              !!soloLayer &&
              soloLayer.thumbUrl &&
              !isAudioType &&
              !failedThumbKeys.has(thumbKey(soloLayer));
            // A YouTube embed URL can't render in a <video> — use its cover image.
            const soloYtThumb = soloLayer ? getYoutubeThumbnailUrl(soloLayer.thumbUrl) : null;
            // Seek the frame grab to this clip's own trimmed in-point (falling
            // back to a hair past 0) — otherwise every piece of a split video
            // shares the same mediaUrl and grabs the identical frame near the
            // start of the source file, making split siblings indistinguishable.
            const soloThumbSeekSec = soloLayer
              ? Math.max(0.1, (soloLayer.trimStart ?? 0) / FPS)
              : 0;

            return (
              <React.Fragment key={row.key}>
                <div
                  className={[
                    "it-track-row",
                    isMain ? "it-track-row--main" : "",
                    rowSelected ? "it-track-row--selected" : "",
                    isDropTarget ? "it-track-row--drop-target" : "",
                  ].join(" ")}
                  data-row-key={row.key}
                  {...(soloLayer ? { "data-layer-id": soloLayer.id } : {})}
                  onClick={soloLayer ? () => handleSelect(soloLayer.id) : undefined}
                >
                  {/* Sticky label */}
                  <div
                    className={[
                      "it-track-label",
                      isMain ? "it-track-label--main" : "",
                      rowSelected ? "it-track-label--selected" : "",
                    ].join(" ")}
                  >
                    <div
                      className={`it-track-label__grip${row.allLocked ? " it-track-label__grip--disabled" : ""}`}
                      title={
                        row.allLocked
                          ? "Kilitli — sıralamak için önce kilidi açın"
                          : row.type === "scene-audio"
                            ? "Sürükleyerek satır sırasını değiştir"
                            : "Sürükleyerek katman sırasını değiştir"
                      }
                      onPointerDown={(e) => handleRowDragStart(e, row)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <LuGripVertical size={12} />
                    </div>
                    {row.isGroup ? (
                      <>
                        <GroupIcon
                          className="it-track-label__icon"
                          style={{ color: rowColor }}
                          title={`${groupLabel} (${row.layers.length})`}
                          size={15}
                        />
                        <span
                          className="it-track-label__count"
                          title={`${groupLabel} (${row.layers.length})`}
                        >
                          {row.layers.length}
                        </span>
                      </>
                    ) : showThumb ? (
                      <div
                        className="it-track-label__thumb-wrap"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          // Clamp so the 140px preview (plus borders/margin) stays inside the viewport.
                          const size = 144;
                          const pad = 8;
                          const x = Math.min(rect.right + 10, window.innerWidth - size - pad);
                          const y = Math.min(
                            Math.max(pad, rect.top),
                            window.innerHeight - size - pad
                          );
                          setThumbPreview({
                            url: soloLayer.thumbUrl,
                            type: soloLayer.type,
                            seekSec: soloThumbSeekSec,
                            x,
                            y,
                          });
                        }}
                        onMouseLeave={() => setThumbPreview(null)}
                      >
                        {soloLayer.type === "video" && !soloYtThumb ? (
                          <VideoThumb
                            className="it-track-label__thumb"
                            url={soloLayer.thumbUrl}
                            seekSec={soloThumbSeekSec}
                            title={soloLayer.name}
                            onFail={() => markThumbFailed(soloLayer)}
                          />
                        ) : (
                          <img
                            className="it-track-label__thumb"
                            src={soloYtThumb || soloLayer.thumbUrl}
                            alt=""
                            title={soloLayer.name}
                            onError={() => markThumbFailed(soloLayer)}
                          />
                        )}
                      </div>
                    ) : SoloIcon ? (
                      <SoloIcon
                        className="it-track-label__icon"
                        style={{ color: rowColor }}
                        title={soloLayer.name}
                        size={15}
                      />
                    ) : (
                      <LuLayoutGrid
                        className="it-track-label__icon"
                        style={{ color: rowColor }}
                        title={soloLayer?.name}
                        size={15}
                      />
                    )}
                    <div className="it-track-label__actions">
                      <button
                        className="it-btn--icon it-btn--xs"
                        title={
                          !row.allVisible ? i18n.t("Show") || "Show" : i18n.t("Hide") || "Hide"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowVisibility(row);
                        }}
                      >
                        {!row.allVisible ? <LuEyeOff size={14} /> : <LuEye size={14} />}
                      </button>
                      <button
                        className="it-btn--icon it-btn--xs"
                        title={
                          row.allLocked ? i18n.t("Unlock") || "Unlock" : i18n.t("Lock") || "Lock"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowLock(row);
                        }}
                      >
                        {row.allLocked ? <LuLock size={14} /> : <LuLockOpen size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* Clips area */}
                  <div className="it-track-clips">
                    {row.layers.map((layer) => {
                      const pluginKey = (
                        pluginToolbars[layer.id]?.pluginId ||
                        layer.box?.pluginId ||
                        ""
                      ).toLowerCase();
                      const isInteractive = pluginKey === "interactiveplugin";
                      const color = TRACK_COLORS[layer.type] || "#6b7280";
                      const clipLeft = frameToX(layer.startFrame || 0);
                      const clipWidth = Math.max(
                        16,
                        frameToX((layer.endFrame || duration) - (layer.startFrame || 0))
                      );
                      const hasChildren =
                        Array.isArray(layer.childLayers) && layer.childLayers.length > 0;
                      const isExpanded = hasChildren && expandedBoxes.has(layer.id);
                      // Snap targets for this clip: every OTHER layer's edges,
                      // plus frame 0, the playhead and the duration marker.
                      const snapTargets = [0, currentFrame, duration];
                      // Nearest neighbor in each direction — drives the context
                      // menu's "align to previous/next" one-click actions. In a
                      // shared row only siblings count (see handleAlignToNeighbor).
                      let prevNeighborEnd = null;
                      let nextNeighborStart = null;
                      layers.forEach((l) => {
                        if (l.id !== layer.id) snapTargets.push(l.startFrame || 0, l.endFrame || 0);
                      });
                      if (row.isGroup) {
                        // Siblings sorted by START, not by gap — so bounds stay
                        // defined even when clips already overlap (a bad state
                        // from before, or an async duration that grew). The
                        // clamp then pushes the dragged clip OUT of the overlap
                        // on its first move instead of letting it float freely.
                        row.layers.forEach((l) => {
                          if (l.id === layer.id) return;
                          const lStart = l.startFrame || 0;
                          const lEnd = l.endFrame || 0;
                          const isBefore =
                            lStart < layer.startFrame ||
                            (lStart === layer.startFrame && l.id < layer.id);
                          if (isBefore) {
                            if (prevNeighborEnd === null || lEnd > prevNeighborEnd)
                              prevNeighborEnd = lEnd;
                          } else if (nextNeighborStart === null || lStart < nextNeighborStart) {
                            nextNeighborStart = lStart;
                          }
                        });
                      } else {
                        layers.forEach((l) => {
                          if (l.id === layer.id) return;
                          const lStart = l.startFrame || 0;
                          const lEnd = l.endFrame || 0;
                          if (
                            lEnd <= layer.startFrame &&
                            (prevNeighborEnd === null || lEnd > prevNeighborEnd)
                          ) {
                            prevNeighborEnd = lEnd;
                          }
                          if (
                            lStart >= layer.endFrame &&
                            (nextNeighborStart === null || lStart < nextNeighborStart)
                          ) {
                            nextNeighborStart = lStart;
                          }
                        });
                      }

                      return (
                        <TrackClip
                          key={layer.id}
                          layer={layer}
                          clipLeft={clipLeft}
                          clipWidth={clipWidth}
                          isSelected={selectedId === layer.id}
                          isNewFlash={!!flashLayerIds && flashLayerIds.has(layer.id)}
                          isDragSource={dragSourceClipId === layer.id}
                          color={color}
                          noResize={isInteractive}
                          pps={pps}
                          fps={FPS}
                          onSelect={handleSelect}
                          onUpdate={handleLayerUpdate}
                          onUpdateEnd={handleLayerUpdateEnd}
                          onSplit={handleSplit}
                          onTrimHead={handleTrimHeadAtPlayhead}
                          currentFrame={currentFrame}
                          onDelete={handleDelete}
                          onToggleLoop={toggleLoop}
                          onToggleVideoLoop={
                            onToggleVideoLoop ? (layerId) => onToggleVideoLoop(layerId) : undefined
                          }
                          onMerlin={onOpenMerlin ? (layerId) => onOpenMerlin(layerId) : undefined}
                          onAnimation={
                            onOpenAnimation ? (layerId) => onOpenAnimation(layerId) : undefined
                          }
                          onDoubleClick={(layerId) => {
                            const l = layersRef.current.find((ll) => ll.id === layerId);
                            if (l) onLayerDoubleClick?.(l);
                          }}
                          expandable={hasChildren}
                          expanded={isExpanded}
                          onToggleExpand={hasChildren ? () => toggleExpanded(layer.id) : undefined}
                          snapTargets={snapTargets}
                          onSnapGuideChange={setSnapGuideFrame}
                          onBeginClipDrag={createClipDragSession}
                          rowNeighborBounds={
                            row.isGroup
                              ? { prevEnd: prevNeighborEnd, nextStart: nextNeighborStart }
                              : null
                          }
                          hasPrevNeighbor={prevNeighborEnd !== null}
                          hasNextNeighbor={nextNeighborStart !== null}
                          onAlignToNeighbor={handleAlignToNeighbor}
                          isSplitHighlighted={
                            !!layer.splitGroup && layer.splitGroup === hoveredSplitGroup
                          }
                          onSplitGroupHover={setHoveredSplitGroup}
                        />
                      );
                    })}
                    {isDropTarget && dragPreview && (
                      <div
                        className={`it-clip it-clip--ghost it-clip--${row.type}`}
                        style={{
                          left: frameToX(dragPreview.startFrame),
                          width: Math.max(
                            16,
                            frameToX(dragPreview.endFrame - dragPreview.startFrame)
                          ),
                          "--clip-color": rowColor,
                        }}
                      />
                    )}
                  </div>
                </div>
                {row.layers.map((layer) => {
                  const hasChildren =
                    Array.isArray(layer.childLayers) && layer.childLayers.length > 0;
                  if (!hasChildren || !expandedBoxes.has(layer.id)) return null;
                  return layer.childLayers.map((child) => (
                    <SubRow
                      key={`${layer.id}::${child.path}`}
                      child={child}
                      parentStartFrame={layer.startFrame || 0}
                      parentEndFrame={layer.endFrame || duration}
                      pps={pps}
                      fps={FPS}
                      isSelected={
                        selectedSubField?.boxId === layer.id &&
                        (selectedSubField.paths || [selectedSubField.path]).includes(child.path)
                      }
                      onSelect={(mods) => {
                        const payload = nextSubFieldSelection(
                          selectedSubField,
                          layer.id,
                          child.path,
                          layer.childLayers.map((c) => c.path),
                          mods
                        );
                        setSelectedSubField(payload);
                        onSelectSubField?.(payload);
                      }}
                      onTimingChange={(partial) =>
                        onSubFieldTimingChange?.({
                          boxId: layer.id,
                          path: child.path,
                          ...partial,
                        })
                      }
                    />
                  ));
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {thumbPreview && (
        <div className="it-thumb-preview" style={{ left: thumbPreview.x, top: thumbPreview.y }}>
          {thumbPreview.type === "video" && !getYoutubeThumbnailUrl(thumbPreview.url) ? (
            <VideoThumb url={thumbPreview.url} seekSec={thumbPreview.seekSec ?? 0.1} />
          ) : (
            <img
              src={getYoutubeThumbnailUrl(thumbPreview.url, "hqdefault") || thumbPreview.url}
              alt=""
            />
          )}
        </div>
      )}
    </div>
  );
});

export default IdeaTimeline;
